import React, { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, BookOpen, Users, FileText, ShoppingBag, ClipboardList, Plus, Megaphone, GraduationCap, Trash2, Pencil, Search, CheckCircle, XCircle, AlertTriangle, Home, LogOut, User, Mail, UserPlus, RefreshCw, Eye, EyeOff, ArrowRight, TrendingUp, Lock, ShieldCheck, Download, Upload } from 'lucide-react';
import { fetchAds, createAd, updateAd, deleteAd, fetchSubjects, createSubject, deleteSubject, updateSubject, fetchTutors, createTutor, updateTutor, deleteTutor, fetchStudents, deleteStudentById, fetchProducts, createProduct, updateProduct, deleteProduct, fetchBlogs, createBlog, updateBlog, deleteBlog, fetchResources, createResource, deleteResource, replaceResourceFile, getResourceFileUrl, API_BASE_URL } from '../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import PdfViewerModal from '../components/PdfViewerModal';

const sections = [
  { key: 'dashboard', label: 'Dashboard',  icon: LayoutDashboard },
  { key: 'products',  label: 'Products',   icon: ShoppingBag     },
  { key: 'tutors',    label: 'Tutors',     icon: Users           },
  { key: 'students',  label: 'Students',   icon: UserPlus        },
  { key: 'resources', label: 'Resources',  icon: BookOpen        },
  { key: 'subjects',  label: 'Subjects',   icon: GraduationCap   },
  { key: 'blogs',     label: 'Blogs',      icon: FileText        },
  { key: 'orders',    label: 'Orders',     icon: ClipboardList   },
  { key: 'ads',       label: 'News & Announcements', icon: Megaphone },
];

// ── Auto-generate helpers ──────────────────────────────────────────────────────
const genUsername = (firstName) => {
  if (!firstName.trim()) return '';
  const base = firstName.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 6);
  const num  = String(Math.floor(Math.random() * 9000) + 1000);
  return (base + num).slice(0, 10);
};
const genPassword = (lastName) => {
  if (!lastName.trim()) return '';
  const syms = ['!', '@', '#', '$', '%', '&'];
  const cap  = lastName[0].toUpperCase();
  const body = lastName.slice(1, 4).toLowerCase() || 'x';
  const n1   = String(Math.floor(Math.random() * 90) + 10);
  const sym  = syms[Math.floor(Math.random() * syms.length)];
  const n2   = String(Math.floor(Math.random() * 900) + 100);
  return `${cap}${body}${n1}${sym}${n2}`;
};

const getSectionFromPath = (pathname) => {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] !== 'admin') return 'dashboard';
  return parts[1] || 'dashboard';
};



const RESOURCE_CATEGORIES = ['Past Papers', 'Revision', 'Books/Notes', 'Short Notes', 'Videos'];
const emptyResourceForm = { title: '', category: 'Past Papers', syllabusType: 'Local', subject: '', year: '', medium: '', videoUrl: '', isPremium: false };

const BLOG_CATEGORIES = ['Exam Tips', 'Study Methods', 'Motivation', 'Subject Guides'];
const emptyBlogForm = { title: '', description: '', content: '', category: 'Exam Tips', author: 'Admin', tags: '', thumbnail: '' };

const initialOrders = [
  { id: 'ORD-1001', customer: 'Nimal', total: 'Rs. 1,500.00', status: 'Pending' },
  { id: 'ORD-1002', customer: 'Kavindu', total: 'Rs. 2,200.00', status: 'Confirmed' },
];

const openDatePicker = (event) => {
  if (typeof event?.target?.showPicker === 'function') {
    try {
      event.target.showPicker();
    } catch (error) {
      // Some browsers require strict user-gesture timing; fallback is native date input behavior.
    }
  }
};

const AD_TITLE_WORD_LIMIT = 8;
const AD_DESCRIPTION_WORD_LIMIT = 24;

const countWords = (value) =>
  value
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;

const limitWords = (value, maxWords) => {
  const words = value.trim().split(/\s+/).filter(Boolean);
  return words.slice(0, maxWords).join(' ');
};

const AdminPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const activeSection = getSectionFromPath(location.pathname);

  // ── Auth gate ──────────────────────────────────────────────────────────────
  const [isAdminAuth, setIsAdminAuth] = useState(() => {
    const u = JSON.parse(localStorage.getItem('userInfo') || '{}');
    return u.role === 'admin';
  });
  const [loginForm, setLoginForm] = useState({ usernameOrEmail: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Login failed.');
      if (data.role !== 'admin') throw new Error('Access denied. Admin credentials required.');
      localStorage.setItem('userInfo', JSON.stringify(data));
      setIsAdminAuth(true);
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setLoginLoading(false);
    }
  };

  const [products, setProducts] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [resources, setResources] = useState([]);
  const [loadingResources, setLoadingResources] = useState(false);
  const [blogs, setBlogs] = useState([]);
  const [orders] = useState(initialOrders);
  const [subjects, setSubjects] = useState([]);
  
  // Ad states
  const [ads, setAds] = useState([]);
  const [adTitle, setAdTitle] = useState('');
  const [adDescription, setAdDescription] = useState('');
  const [adImageUrl, setAdImageUrl] = useState('');
  const [adStartDate, setAdStartDate] = useState('');
  const [adEndDate, setAdEndDate] = useState('');
  const [adType, setAdType] = useState('announcement');
  const [editingAdId, setEditingAdId] = useState('');
  const [isPostingAd, setIsPostingAd] = useState(false);
  const [adFeedback, setAdFeedback] = useState({ type: '', message: '' });

  const emptyProductForm = { title: '', description: '', price: '', syllabus: 'Local', subject: '', level: 'O/L', stock: '', imageUrl: '', isAvailable: true };
  const [productForm, setProductForm] = useState(emptyProductForm);
  const [productImageUrl, setProductImageUrl] = useState('');
  const [editingProductId, setEditingProductId] = useState('');
  const [isSavingProduct, setIsSavingProduct] = useState(false);
  const [productFeedback, setProductFeedback] = useState({ type: '', message: '' });
  const [productSearch, setProductSearch] = useState('');
  const [tutorForm, setTutorForm] = useState({ firstName: '', lastName: '', email: '', password: '', username: '', syllabus: [], mode: 'Online', area: '', tags: '', isPaidAd: false });
  const [showTutorPassword, setShowTutorPassword] = useState(false);
  const [tutorSubjects, setTutorSubjects] = useState([]);
  const [subjectInput, setSubjectInput] = useState({ level: 'O/L', subjectName: '' });
  const [editingTutorId, setEditingTutorId] = useState('');
  const [isSavingTutor, setIsSavingTutor] = useState(false);
  const [tutorFeedback, setTutorFeedback] = useState({ type: '', message: '' });
  // Student states
  const [students, setStudents] = useState([]);
  const emptyStudentForm = { firstName: '', lastName: '', email: '', phone: '', age: '', level: 'O/L', syllabus: [], username: '', password: '' };
  const [studentForm, setStudentForm] = useState(emptyStudentForm);
  const [studentSubjects, setStudentSubjects] = useState([]);
  const [studentSubjectInput, setStudentSubjectInput] = useState('');
  const [isSavingStudent, setIsSavingStudent] = useState(false);
  const [studentFeedback, setStudentFeedback] = useState({ type: '', message: '' });
  const [showStudentPassword, setShowStudentPassword] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const [editingStudentId, setEditingStudentId] = useState('');

  const [resourceForm, setResourceForm] = useState(emptyResourceForm);
  const [resourceFile, setResourceFile] = useState(null);
  const [isUploadingResource, setIsUploadingResource] = useState(false);
  const [resourceFeedback, setResourceFeedback] = useState({ type: '', message: '' });
  const [previewResource, setPreviewResource] = useState(null);
  const [replacingResourceId, setReplacingResourceId] = useState('');
  const [blogForm, setBlogForm]         = useState(emptyBlogForm);
  const [editingBlogId, setEditingBlogId] = useState('');
  const [isSavingBlog, setIsSavingBlog]   = useState(false);
  const [blogFeedback, setBlogFeedback]   = useState({ type: '', message: '' });
  const [blogThumbnail, setBlogThumbnail] = useState('');
  const [subjectForm, setSubjectForm] = useState({ name: '', syllabus: 'Local', level: 'O/L' });
  const [editingSubjectId, setEditingSubjectId] = useState('');
  const [subjectSearchTerm] = useState('');
  const [confirmation, setConfirmation] = useState({ isOpen: false, title: '', message: '', onConfirm: () => {} });
  const [toast, setToast] = useState({ isVisible: false, message: '', type: 'success' });

  const stats = useMemo(
    () => [
      { label: 'Products',    value: products.length,  icon: ShoppingBag,    color: 'text-blue-400',    bg: 'bg-blue-400/10',    key: 'products'  },
      { label: 'Teachers',    value: tutors.length,    icon: Users,           color: 'text-green-400',   bg: 'bg-green-400/10',   key: 'tutors'    },
      { label: 'Students',    value: students.length,  icon: UserPlus,        color: 'text-cyan-400',    bg: 'bg-cyan-400/10',    key: 'students'  },
      { label: 'Subjects',    value: subjects.length,  icon: GraduationCap,   color: 'text-orange-400',  bg: 'bg-orange-400/10',  key: 'subjects'  },
      { label: 'Announcements', value: ads.length,      icon: Megaphone,       color: 'text-accentBlue',  bg: 'bg-accentBlue/10',  key: 'ads'       },
    ],
    [products.length, tutors.length, students.length, subjects.length, ads.length]
  );

  const adminUserInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good Morning';
    if (h < 17) return 'Good Afternoon';
    return 'Good Evening';
  };
  const todayStr = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  useEffect(() => {
    fetchAds().then(res => setAds(res.data)).catch(console.error);
    fetchSubjects().then(res => setSubjects(res.data)).catch(console.error);
    fetchTutors().then(res => setTutors(res.data)).catch(console.error);
    fetchStudents().then(res => setStudents(res.data)).catch(console.error);
    fetchProducts().then(res => setProducts(res.data)).catch(console.error);
    fetchBlogs().then(res => setBlogs(res.data)).catch(console.error);
    setLoadingResources(true);
    fetchResources().then(res => setResources(res.data)).catch(console.error).finally(() => setLoadingResources(false));
  }, []);

  // ── Student helpers ────────────────────────────────────────────────────────
  const regenStudentCreds = (firstName, lastName) => {
    setStudentForm(prev => ({
      ...prev,
      username: genUsername(firstName ?? prev.firstName),
      password: genPassword(lastName  ?? prev.lastName),
    }));
  };

  const handleStudentNameChange = (field, value) => {
    setStudentForm(prev => {
      const updated = { ...prev, [field]: value };
      return {
        ...updated,
        username: genUsername(field === 'firstName' ? value : prev.firstName),
        password: genPassword(field === 'lastName'  ? value : prev.lastName),
      };
    });
  };

  const handleAddStudentSubject = () => {
    if (!studentSubjectInput) return;
    if (studentSubjects.some(s => s.subjectName === studentSubjectInput)) return;
    setStudentSubjects(prev => [...prev, { subjectName: studentSubjectInput }]);
    setStudentSubjectInput('');
  };

  const resetStudentForm = () => {
    setStudentForm(emptyStudentForm);
    setStudentSubjects([]);
    setStudentSubjectInput('');
    setStudentFeedback({ type: '', message: '' });
    setEditingStudentId('');
  };

  const handleEditStudent = (st) => {
    setEditingStudentId(st._id);
    setStudentForm({
      firstName: st.firstName || '',
      lastName:  st.lastName  || '',
      email:     st.email     || '',
      phone:     st.phone     || '',
      age:       st.age       != null ? String(st.age) : '',
      level:     st.level     || 'O/L',
      syllabus:  st.syllabus  || [],
      username:  st.username  || '',
      password:  '',
    });
    setStudentSubjects(st.subjects || []);
    setStudentSubjectInput('');
    setStudentFeedback({ type: '', message: '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleRegisterStudent = async (e) => {
    e.preventDefault();
    const { firstName, lastName, email, phone, age, level, syllabus, username, password } = studentForm;
    if (!firstName.trim() || !lastName.trim()) return setStudentFeedback({ type: 'error', message: 'First and last name are required.' });
    if (!email.trim())    return setStudentFeedback({ type: 'error', message: 'Email is required.' });
    if (!username.trim()) return setStudentFeedback({ type: 'error', message: 'Username is required.' });
    if (!editingStudentId && !password.trim()) return setStudentFeedback({ type: 'error', message: 'Password is required.' });
    if (syllabus.length === 0) return setStudentFeedback({ type: 'error', message: 'Select at least one syllabus.' });

    try {
      setIsSavingStudent(true);
      setStudentFeedback({ type: '', message: '' });

      const payload = {
        firstName, lastName, email, phone,
        age: age ? Number(age) : undefined,
        level, syllabus, subjects: studentSubjects,
        username, role: 'student',
      };
      if (password.trim()) payload.password = password;

      if (editingStudentId) {
        const res = await fetch(`${API_BASE_URL}/auth/students/${editingStudentId}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Update failed.');
        setStudents(prev => prev.map(st => st._id === editingStudentId ? data : st));
        showToast(`Student "${firstName} ${lastName}" updated successfully!`);
      } else {
        const res = await fetch(`${API_BASE_URL}/auth/register`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Registration failed.');
        setStudents(prev => [data, ...prev]);
        showToast(`Student "${firstName} ${lastName}" registered successfully!`);
      }

      resetStudentForm();
    } catch (err) {
      setStudentFeedback({ type: 'error', message: err.message });
    } finally {
      setIsSavingStudent(false);
    }
  };

  const handleDeleteStudent = (id) => {
    const s = students.find(st => st._id === id);
    const name = s ? `"${s.firstName} ${s.lastName}"` : 'this student';
    setConfirmation({
      isOpen: true, title: 'Delete Student',
      message: `Are you sure you want to delete ${name}? This cannot be undone.`,
      onConfirm: async () => {
        try {
          await deleteStudentById(id);
          setStudents(prev => prev.filter(st => st._id !== id));
          showToast('Student deleted.');
        } catch { showToast('Failed to delete student.', 'error'); }
      },
    });
  };

  // ── Product helpers ────────────────────────────────────────────────────────
  const resetProductForm = () => {
    setProductForm(emptyProductForm);
    setProductImageUrl('');
    setEditingProductId('');
    setProductFeedback({ type: '', message: '' });
  };

  const handleProductImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setProductFeedback({ type: 'error', message: 'Image must be smaller than 2MB.' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const url = typeof reader.result === 'string' ? reader.result : '';
      setProductImageUrl(url);
      setProductForm(prev => ({ ...prev, imageUrl: url }));
      setProductFeedback({ type: '', message: '' });
    };
    reader.readAsDataURL(file);
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    if (!productForm.title.trim()) return setProductFeedback({ type: 'error', message: 'Title is required.' });
    if (!productForm.description.trim()) return setProductFeedback({ type: 'error', message: 'Description is required.' });
    if (!productForm.price || isNaN(Number(productForm.price))) return setProductFeedback({ type: 'error', message: 'Valid price is required.' });
    if (!productForm.subject.trim()) return setProductFeedback({ type: 'error', message: 'Subject is required.' });

    const payload = {
      ...productForm,
      price: Number(productForm.price),
      stock: Number(productForm.stock) || 0,
    };

    try {
      setIsSavingProduct(true);
      setProductFeedback({ type: '', message: '' });
      if (editingProductId) {
        const res = await updateProduct(editingProductId, payload);
        setProducts(prev => prev.map(p => p._id === editingProductId ? res.data : p));
        showToast('Product updated!');
      } else {
        const res = await createProduct(payload);
        setProducts(prev => [res.data, ...prev]);
        showToast('Product added!');
      }
      resetProductForm();
    } catch (err) {
      setProductFeedback({ type: 'error', message: err?.response?.data?.message || 'Failed to save product.' });
    } finally {
      setIsSavingProduct(false);
    }
  };

  const handleEditProduct = (p) => {
    setEditingProductId(p._id);
    setProductForm({
      title: p.title, description: p.description,
      price: String(p.price), syllabus: p.syllabus,
      subject: p.subject, level: p.level,
      stock: String(p.stock ?? ''), imageUrl: p.imageUrl || '',
      isAvailable: p.isAvailable,
    });
    setProductImageUrl(p.imageUrl || '');
    setProductFeedback({ type: '', message: '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteProduct = (id) => {
    const p = products.find(x => x._id === id);
    setConfirmation({
      isOpen: true, title: 'Delete Product',
      message: `Delete "${p?.title || 'this product'}"? This cannot be undone.`,
      onConfirm: async () => {
        try {
          await deleteProduct(id);
          setProducts(prev => prev.filter(x => x._id !== id));
          if (editingProductId === id) resetProductForm();
          showToast('Product deleted.');
        } catch { showToast('Failed to delete product.', 'error'); }
      },
    });
  };

  const goToSection = (key) => {
    navigate(key === 'dashboard' ? '/admin' : `/admin/${key}`);
  };


  const handleAddTutorSubject = () => {
    if (!subjectInput.subjectName) return;
    const alreadyAdded = tutorSubjects.some(
      (s) => s.subjectName === subjectInput.subjectName && s.levels.includes(subjectInput.level)
    );
    if (alreadyAdded) return;
    setTutorSubjects((prev) => [...prev, { subjectName: subjectInput.subjectName, levels: [subjectInput.level] }]);
    setSubjectInput((prev) => ({ ...prev, subjectName: '' }));
  };

  const emptyTutorForm = { firstName: '', lastName: '', email: '', password: '', username: '', syllabus: [], mode: 'Online', area: '', tags: '', isPaidAd: false };

  const regenTutorCreds = () => {
    setTutorForm(prev => ({
      ...prev,
      username: genUsername(prev.firstName),
      password: genPassword(prev.lastName),
    }));
  };

  const handleTutorNameChange = (field, value) => {
    setTutorForm(prev => {
      const updated = { ...prev, [field]: value };
      return {
        ...updated,
        username: genUsername(field === 'firstName' ? value : prev.firstName),
        password: genPassword(field === 'lastName'  ? value : prev.lastName),
      };
    });
  };

  const handleCancelEditTutor = () => {
    setEditingTutorId('');
    setTutorForm(emptyTutorForm);
    setTutorSubjects([]);
    setSubjectInput({ level: 'O/L', subjectName: '' });
    setTutorFeedback({ type: '', message: '' });
  };

  const handleTutorSubmit = async (e) => {
    e.preventDefault();
    if (!tutorForm.firstName.trim() || !tutorForm.lastName.trim()) return setTutorFeedback({ type: 'error', message: 'Please enter first and last name.' });
    if (!tutorForm.email.trim()) return setTutorFeedback({ type: 'error', message: 'Please enter an email address.' });
    if (!tutorForm.username.trim()) return setTutorFeedback({ type: 'error', message: 'Username is required.' });
    if (!editingTutorId && !tutorForm.password.trim()) return setTutorFeedback({ type: 'error', message: 'Please set a password for the teacher.' });
    if (tutorForm.syllabus.length === 0) return setTutorFeedback({ type: 'error', message: 'Please select at least one syllabus.' });
    if (!tutorForm.area.trim()) return setTutorFeedback({ type: 'error', message: 'Please enter an area.' });

    const payload = {
      name:      `${tutorForm.firstName} ${tutorForm.lastName}`.trim(),
      firstName: tutorForm.firstName,
      lastName:  tutorForm.lastName,
      email:     tutorForm.email,
      username:  tutorForm.username,
      syllabus:  tutorForm.syllabus,
      mode:      tutorForm.mode,
      area:      tutorForm.area,
      tags:      tutorForm.tags.split(',').map((t) => t.trim()).filter(Boolean),
      isPaidAd:  tutorForm.isPaidAd,
      subjects:  tutorSubjects,
    };
    if (tutorForm.password.trim()) payload.password = tutorForm.password;

    try {
      setIsSavingTutor(true);
      setTutorFeedback({ type: '', message: '' });
      if (editingTutorId) {
        const res = await updateTutor(editingTutorId, payload);
        setTutors((prev) => prev.map((t) => (t._id === editingTutorId ? res.data : t)));
        showToast('Teacher updated successfully!', 'success');
      } else {
        const res = await createTutor(payload);
        setTutors((prev) => [...prev, res.data]);
        showToast('Teacher registered successfully!', 'success');
      }
      handleCancelEditTutor();
    } catch (err) {
      console.error(err);
      setTutorFeedback({ type: 'error', message: err?.response?.data?.message || 'Failed to save teacher.' });
    } finally {
      setIsSavingTutor(false);
    }
  };

  const handleEditTutor = (tutor) => {
    setEditingTutorId(tutor._id);
    setTutorForm({
      firstName: tutor.firstName || tutor.name?.split(' ')[0] || '',
      lastName:  tutor.lastName  || tutor.name?.split(' ').slice(1).join(' ') || '',
      email:     tutor.email    || '',
      password:  '',
      username:  tutor.username || '',
      syllabus:  tutor.syllabus || [],
      mode:      tutor.mode,
      area:      tutor.area,
      tags:      tutor.tags?.join(', ') || '',
      isPaidAd:  tutor.isPaidAd || false,
    });
    setTutorSubjects(tutor.subjects || []);
    setTutorFeedback({ type: '', message: '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteTutor = (id) => {
    const tutorToDelete = tutors.find((t) => t._id === id);
    const tutorName = tutorToDelete ? `"${tutorToDelete.name}"` : 'this teacher';

    const performDelete = async () => {
      try {
        await deleteTutor(id);
        setTutors((prev) => prev.filter((t) => t._id !== id));
        if (editingTutorId === id) handleCancelEditTutor();
        showToast('Teacher deleted successfully.', 'success');
      } catch (err) {
        console.error(err);
        showToast(err.response?.data?.message || 'Error deleting teacher.', 'error');
      }
    };

    setConfirmation({ isOpen: true, title: 'Confirm Deletion', message: `Are you sure you want to delete ${tutorName}?`, onConfirm: performDelete });
  };

  const handleAddResource = async (e) => {
    e.preventDefault();
    setResourceFeedback({ type: '', message: '' });

    if (!resourceForm.title.trim() || !resourceForm.subject) {
      setResourceFeedback({ type: 'error', message: 'Title and subject are required.' });
      return;
    }
    if (resourceForm.category !== 'Videos' && !resourceFile) {
      setResourceFeedback({ type: 'error', message: 'Please select a PDF file to upload.' });
      return;
    }
    if (resourceForm.category === 'Videos' && !resourceForm.videoUrl.trim()) {
      setResourceFeedback({ type: 'error', message: 'Please provide a video URL.' });
      return;
    }

    setIsUploadingResource(true);
    try {
      const fd = new FormData();
      fd.append('title', resourceForm.title.trim());
      fd.append('syllabusType', resourceForm.syllabusType);
      fd.append('category', resourceForm.category);
      fd.append('subject', resourceForm.subject);
      if (resourceForm.year) fd.append('year', resourceForm.year);
      if (resourceForm.medium) fd.append('medium', resourceForm.medium);
      if (resourceForm.videoUrl) fd.append('videoUrl', resourceForm.videoUrl.trim());
      fd.append('isPremium', resourceForm.isPremium);
      fd.append('uploadedBy', 'admin');
      if (resourceFile) fd.append('file', resourceFile);

      const res = await createResource(fd);
      setResources((prev) => [res.data, ...prev]);
      setResourceForm(emptyResourceForm);
      setResourceFile(null);
      showToast('Resource uploaded successfully!');
    } catch (err) {
      setResourceFeedback({ type: 'error', message: err.response?.data?.message || 'Upload failed.' });
    } finally {
      setIsUploadingResource(false);
    }
  };

  const handleDeleteResource = (id, title) => {
    const performDelete = async () => {
      try {
        await deleteResource(id);
        setResources((prev) => prev.filter((r) => r._id !== id));
        showToast('Resource deleted successfully!');
      } catch (err) {
        showToast(err.response?.data?.message || 'Error deleting resource.', 'error');
      }
    };
    setConfirmation({ isOpen: true, title: 'Confirm Deletion', message: `Are you sure you want to delete "${title}"?`, onConfirm: performDelete });
  };

  const handleReplaceResourceFile = async (id, file) => {
    if (!file) return;

    setReplacingResourceId(id);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await replaceResourceFile(id, formData);

      setResources((prev) => prev.map((item) => (item._id === id ? res.data : item)));
      setPreviewResource((current) => (current?._id === id ? res.data : current));
      showToast('PDF replaced successfully!');
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not replace the PDF.', 'error');
    } finally {
      setReplacingResourceId('');
    }
  };

  const resourceSubjectOptions = subjects.filter((s) => s.syllabus === resourceForm.syllabusType);

  // ── Blog handlers ─────────────────────────────────────────────────────────
  const resetBlogForm = () => {
    setBlogForm(emptyBlogForm);
    setBlogThumbnail('');
    setEditingBlogId('');
    setBlogFeedback({ type: '', message: '' });
  };

  const handleBlogThumbnail = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { setBlogFeedback({ type: 'error', message: 'Image must be under 2MB.' }); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const url = typeof reader.result === 'string' ? reader.result : '';
      setBlogThumbnail(url);
      setBlogForm(p => ({ ...p, thumbnail: url }));
    };
    reader.readAsDataURL(file);
  };

  const handleBlogSubmit = async (e) => {
    e.preventDefault();
    if (!blogForm.title.trim())       return setBlogFeedback({ type: 'error', message: 'Title is required.' });
    if (!blogForm.description.trim()) return setBlogFeedback({ type: 'error', message: 'Description is required.' });
    if (!blogForm.content.trim())     return setBlogFeedback({ type: 'error', message: 'Content is required.' });
    const payload = { ...blogForm, tags: blogForm.tags.split(',').map(t => t.trim()).filter(Boolean) };
    try {
      setIsSavingBlog(true);
      setBlogFeedback({ type: '', message: '' });
      if (editingBlogId) {
        const res = await updateBlog(editingBlogId, payload);
        setBlogs(prev => prev.map(b => b._id === editingBlogId ? res.data : b));
        showToast('Blog post updated!');
      } else {
        const res = await createBlog(payload);
        setBlogs(prev => [res.data, ...prev]);
        showToast('Blog post published!');
      }
      resetBlogForm();
    } catch (err) {
      setBlogFeedback({ type: 'error', message: err?.response?.data?.message || 'Failed to save post.' });
    } finally {
      setIsSavingBlog(false);
    }
  };

  const handleEditBlog = (blog) => {
    setEditingBlogId(blog._id);
    setBlogForm({
      title:       blog.title,
      description: blog.description,
      content:     blog.content,
      category:    blog.category,
      author:      blog.author || 'Admin',
      tags:        (blog.tags || []).join(', '),
      thumbnail:   blog.thumbnail || '',
    });
    setBlogThumbnail(blog.thumbnail || '');
    setBlogFeedback({ type: '', message: '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteBlog = (id) => {
    const b = blogs.find(x => x._id === id);
    setConfirmation({
      isOpen: true, title: 'Delete Post',
      message: `Delete "${b?.title || 'this post'}"? This cannot be undone.`,
      onConfirm: async () => {
        try {
          await deleteBlog(id);
          setBlogs(prev => prev.filter(x => x._id !== id));
          if (editingBlogId === id) resetBlogForm();
          showToast('Blog post deleted.');
        } catch { showToast('Failed to delete post.', 'error'); }
      },
    });
  };

  // Handle Add Ad
  const handleAddAd = async (e) => {
    e.preventDefault();
    if (!adTitle.trim() || !adDescription.trim()) {
      setAdFeedback({ type: 'error', message: 'Please add a title and description.' });
      return;
    }

    if (countWords(adTitle) > AD_TITLE_WORD_LIMIT) {
      setAdFeedback({ type: 'error', message: `Ad title can have maximum ${AD_TITLE_WORD_LIMIT} words.` });
      return;
    }

    if (countWords(adDescription) > AD_DESCRIPTION_WORD_LIMIT) {
      setAdFeedback({ type: 'error', message: `Ad description can have maximum ${AD_DESCRIPTION_WORD_LIMIT} words.` });
      return;
    }

    try {
      setIsPostingAd(true);
      setAdFeedback({ type: '', message: '' });
      const payload = {
        title: adTitle,
        description: adDescription,
        imageUrl: adImageUrl,
        startDate: adStartDate || undefined,
        endDate: adEndDate || undefined,
        type: adType,
      };

      if (editingAdId) {
        const res = await updateAd(editingAdId, payload);
        setAds((prev) => prev.map((ad) => (ad._id === editingAdId ? res.data : ad)));
        setAdFeedback({ type: 'success', message: `${adType === 'news' ? 'News' : 'Announcement'} updated successfully.` });
      } else {
        const res = await createAd(payload);
        setAds((prev) => [res.data, ...prev]);
        setAdFeedback({ type: 'success', message: `${adType === 'news' ? 'News' : 'Announcement'} published successfully.` });
      }

      setAdTitle('');
      setAdDescription('');
      setAdImageUrl('');
      setAdStartDate('');
      setAdEndDate('');
      setAdType('announcement');
      setEditingAdId('');
    } catch (err) {
      console.error(err);
      setAdFeedback({ type: 'error', message: err?.response?.data?.message || 'Failed to save announcement. Please try again.' });
    } finally {
      setIsPostingAd(false);
    }
  };

  const handleSubjectSubmit = async (e) => {
    e.preventDefault();
    if (!subjectForm.name.trim()) return showToast('Please enter a subject name.', 'error');

    const isUpdating = !!editingSubjectId;
    const title = isUpdating ? 'Confirm Update' : 'Confirm Add Subject';
    const message = isUpdating
      ? `Are you sure you want to save changes to this subject?`
      : `Are you sure you want to add "${subjectForm.name}" as a new subject?`;

    const performSubmit = async () => {
        try {
          if (isUpdating) {
            const res = await updateSubject(editingSubjectId, subjectForm);
            setSubjects((prev) => prev.map((s) => (s._id === editingSubjectId ? res.data : s)));
            showToast('Subject updated successfully!', 'success');
          } else {
            const res = await createSubject(subjectForm);
            setSubjects((prev) => [...prev, res.data]);
            showToast('Subject added successfully!', 'success');
          }
          setEditingSubjectId('');
          setSubjectForm({ name: '', syllabus: 'Local', level: 'O/L' });
        } catch (err) {
          console.error(err);
          showToast(err.response?.data?.message || 'An error occurred.', 'error');
        }
    };

    setConfirmation({ isOpen: true, title, message, onConfirm: performSubmit });
  };

  const handleDeleteSubject = async (id) => {
    const subjectToDelete = subjects.find((s) => s._id === id);
    const subjectName = subjectToDelete ? `"${subjectToDelete.name}"` : 'this subject';

    const performDelete = async () => {
      try {
        await deleteSubject(id);
        setSubjects((prev) => prev.filter((s) => s._id !== id));
        if (editingSubjectId === id) {
          handleCancelEditSubject();
        }
        showToast('Subject deleted successfully.', 'success');
      } catch (err) {
        console.error(err);
        showToast(err.response?.data?.message || 'Error deleting subject.', 'error');
      }
    };

    setConfirmation({ isOpen: true, title: 'Confirm Deletion', message: `Are you sure you want to delete ${subjectName}? This action cannot be undone.`, onConfirm: performDelete });
  };
  const handleEditAd = (ad) => {
    setEditingAdId(ad._id);
    setAdTitle(ad.title || '');
    setAdDescription(ad.description || ad.text || '');
    setAdImageUrl(ad.imageUrl || '');
    setAdStartDate(ad.startDate ? new Date(ad.startDate).toISOString().slice(0, 10) : '');
    setAdEndDate(ad.endDate ? new Date(ad.endDate).toISOString().slice(0, 10) : '');
    setAdType(ad.type || 'announcement');
    setAdFeedback({ type: '', message: '' });
    
    // Auto scroll to top when edit is clicked
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEditSubject = (subject) => {
    setEditingSubjectId(subject._id);
    setSubjectForm({
      name: subject.name,
      syllabus: subject.syllabus,
      level: subject.level,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEditSubject = () => {
    setEditingSubjectId('');
    setSubjectForm({ name: '', syllabus: 'Local', level: 'O/L' });
  };

  const handleCancelEdit = () => {
    setEditingAdId('');
    setAdTitle('');
    setAdDescription('');
    setAdImageUrl('');
    setAdStartDate('');
    setAdEndDate('');
    setAdType('announcement');
    setAdFeedback({ type: '', message: '' });
  };

  const handleDeleteAd = async (id) => {
    const performDelete = async () => {
      try {
        await deleteAd(id);
        setAds((prev) => prev.filter((ad) => ad._id !== id));

        if (editingAdId === id) {
          handleCancelEdit();
        }

        setAdFeedback({ type: 'success', message: 'Announcement deleted successfully.' });
      } catch (err) {
        console.error(err);
        setAdFeedback({ type: 'error', message: err?.response?.data?.message || 'Failed to delete announcement. Please try again.' });
      }
    };

    setConfirmation({
      isOpen: true,
      title: 'Confirm Deletion',
      message: 'Are you sure you want to delete this ad?',
      onConfirm: performDelete,
    });
  };


  const showToast = (message, type = 'success') => {
    setToast({ isVisible: true, message, type });
    setTimeout(() => {
      setToast({ isVisible: false, message: '', type });
    }, 4000);
  };

  const filteredSubjects = subjects.filter((subject) => {
    const term = subjectSearchTerm.toLowerCase();
    return (
      subject.name.toLowerCase().includes(term) ||
      subject.syllabus.toLowerCase().includes(term) ||
      subject.level.toLowerCase().includes(term)
    );
  });

  // ── Login gate render ─────────────────────────────────────────────────────
  if (!isAdminAuth) {
    return (
      <div className="min-h-screen bg-darkBg flex items-center justify-center px-4 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-5%] w-[500px] h-[500px] bg-accentBlue/5 rounded-full blur-[140px]" />
          <div className="absolute bottom-[-10%] right-[-5%] w-[400px] h-[400px] bg-purple-500/5 rounded-full blur-[120px]" />
          <div className="absolute top-[40%] left-[50%] w-[300px] h-[300px] bg-accentBlue/3 rounded-full blur-[100px]" />
        </div>

        <div className="w-full max-w-md relative z-10">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-accentBlue/10 border border-accentBlue/20 mb-5 shadow-lg shadow-accentBlue/10">
              <ShieldCheck size={30} className="text-accentBlue" />
            </div>
            <div className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-accentBlue/10 border border-accentBlue/20 rounded-full text-accentBlue text-[10px] font-bold uppercase tracking-[0.18em] mb-4 block mx-auto w-fit">
              <Lock size={10} /> Admin Portal
            </div>
            <h1 className="text-3xl font-extrabold text-white tracking-tight">Admin Login</h1>
            <p className="text-gray-500 text-sm mt-2">Restricted access. Authorized personnel only.</p>
          </div>

          {/* Card */}
          <div className="bg-white/4 border border-white/10 rounded-[28px] p-8 backdrop-blur-xl shadow-2xl">
            {loginError && (
              <div className="flex items-center gap-2.5 bg-red-500/10 border border-red-500/25 text-red-400 p-4 rounded-xl mb-6 text-sm">
                <AlertTriangle size={15} className="shrink-0" />
                {loginError}
              </div>
            )}

            <form onSubmit={handleAdminLogin} className="space-y-5">
              <div>
                <label className="block text-xs text-gray-400 mb-2 uppercase tracking-wider font-medium">Email or Username</label>
                <input
                  type="text"
                  value={loginForm.usernameOrEmail}
                  onChange={e => setLoginForm(p => ({ ...p, usernameOrEmail: e.target.value }))}
                  placeholder="admin@example.com"
                  required
                  autoFocus
                  className="w-full bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-400 mb-2 uppercase tracking-wider font-medium">Password</label>
                <div className="relative">
                  <input
                    type={showLoginPassword ? 'text' : 'password'}
                    value={loginForm.password}
                    onChange={e => setLoginForm(p => ({ ...p, password: e.target.value }))}
                    placeholder="••••••••"
                    required
                    className="w-full bg-darkBg border border-white/10 p-3.5 pr-12 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowLoginPassword(v => !v)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                  >
                    {showLoginPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loginLoading}
                className="w-full py-3.5 bg-accentBlue text-black font-bold rounded-xl hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm mt-2"
              >
                {loginLoading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Authenticating...
                  </>
                ) : (
                  <>
                    <ShieldCheck size={16} /> Access Dashboard
                  </>
                )}
              </button>
            </form>
          </div>

          <p className="text-center text-gray-600 text-[11px] mt-6 tracking-wide">
            SMART EDU NEXT &mdash; Admin Console &mdash; Unauthorized access is prohibited.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen flex flex-col">
      <AdminNavbar onLogout={() => { localStorage.removeItem('userInfo'); setIsAdminAuth(false); }} />
      <div className="max-w-7xl mx-auto w-full px-6 py-10">
      <Toast
        isVisible={toast.isVisible}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isVisible: false })}
      />
      <ConfirmationModal
        isOpen={confirmation.isOpen}
        title={confirmation.title}
        message={confirmation.message}
        onConfirm={() => { confirmation.onConfirm(); setConfirmation({ isOpen: false }); }}
        onClose={() => setConfirmation({ isOpen: false })}
      />

      <header className="mb-10">
        <h1 className="text-4xl font-bold text-accentBlue mb-2">Admin Dashboard</h1>
        <p className="text-gray-400 text-sm">Manage your platform's content, users, and settings.</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <aside className="lg:col-span-1 bg-white/5 border border-white/10 rounded-3xl p-5 h-fit sticky top-24 backdrop-blur-xl z-10">
          <div className="space-y-2">
            {sections.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => goToSection(key)}
                className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium transition-all ${
                  activeSection === key
                    ? 'bg-accentBlue/10 border border-accentBlue/30 text-accentBlue shadow-[0_0_15px_rgba(var(--accentBlue),0.1)]'
                    : 'bg-transparent border border-transparent text-gray-400 hover:bg-white/5 hover:text-white'
                }`}
              >
                <Icon size={16} /> {label}
              </button>
            ))}
          </div>
        </aside>

        <section className="lg:col-span-3 space-y-6">
          {activeSection === 'dashboard' && (
            <div className="space-y-6">

              {/* Welcome Banner */}
              <div className="bg-gradient-to-r from-accentBlue/20 via-accentBlue/10 to-transparent border border-accentBlue/20 rounded-3xl p-7">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5">
                  <div>
                    <p className="text-sm text-accentBlue font-bold uppercase tracking-widest mb-1">
                      {getGreeting()}, {adminUserInfo.firstName || 'Admin'} 👋
                    </p>
                    <h2 className="text-3xl font-extrabold text-white">Admin Dashboard</h2>
                    <p className="text-gray-400 text-sm mt-1">{todayStr}</p>
                  </div>
                  <div className="flex gap-3 shrink-0">
                    <div className="bg-white/5 border border-white/10 rounded-2xl px-5 py-3 text-center min-w-[90px]">
                      <p className="text-2xl font-bold text-white">{students.length + tutors.length}</p>
                      <p className="text-[11px] text-gray-400 mt-0.5">Total Users</p>
                    </div>
                    <div className="bg-accentBlue/10 border border-accentBlue/30 rounded-2xl px-5 py-3 text-center min-w-[90px]">
                      <p className="text-2xl font-bold text-accentBlue">{products.length}</p>
                      <p className="text-[11px] text-gray-400 mt-0.5">Products</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* KPI Stat Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                {stats.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button key={item.label} onClick={() => goToSection(item.key)}
                      className="bg-white/5 border border-white/10 rounded-2xl p-5 hover:border-white/25 hover:-translate-y-1 transition-all duration-300 group text-left w-full">
                      <div className={`p-2.5 rounded-xl ${item.bg} ${item.color} w-fit mb-4 group-hover:scale-110 transition-transform`}>
                        <Icon size={20} />
                      </div>
                      <p className="text-3xl font-extrabold text-white">{item.value}</p>
                      <p className="text-xs text-gray-400 mt-1 font-medium">{item.label}</p>
                      <div className={`flex items-center gap-1 mt-2 text-[10px] font-semibold ${item.color} opacity-70`}>
                        <TrendingUp size={10} /> View all
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Main 2-col grid */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                {/* Left 2/3 */}
                <div className="lg:col-span-2 space-y-6">

                  {/* Recent Students */}
                  <div className="bg-white/5 border border-white/10 rounded-3xl p-6">
                    <div className="flex items-center justify-between mb-5">
                      <h3 className="font-bold text-base flex items-center gap-2">
                        <UserPlus size={16} className="text-cyan-400" /> Recent Students
                      </h3>
                      <button onClick={() => goToSection('students')} className="text-xs text-accentBlue hover:underline flex items-center gap-1">
                        View all <ArrowRight size={12} />
                      </button>
                    </div>
                    {students.length === 0 ? (
                      <p className="text-gray-500 text-sm text-center py-8">No students registered yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {students.slice(0, 5).map((st) => (
                          <div key={st._id} className="flex items-center gap-3 p-3 bg-darkBg rounded-2xl border border-white/5 hover:border-white/10 transition-colors">
                            <div className="w-9 h-9 rounded-full bg-cyan-500/10 text-cyan-400 flex items-center justify-center text-sm font-bold shrink-0">
                              {(st.firstName?.[0] || '?')}{(st.lastName?.[0] || '')}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm text-white truncate">{st.firstName} {st.lastName}</p>
                              <p className="text-xs text-gray-400 truncate">{st.email}</p>
                            </div>
                            <div className="flex gap-1.5 shrink-0">
                              {st.level && <span className="text-[10px] px-2 py-0.5 bg-purple-500/10 text-purple-400 rounded-full font-semibold">{st.level}</span>}
                              {(st.syllabus || []).slice(0, 1).map((sy) => (
                                <span key={sy} className="text-[10px] px-2 py-0.5 bg-white/10 text-gray-300 rounded-full">{sy}</span>
                              ))}
                            </div>
                          </div>
                        ))}
                        {students.length > 5 && <p className="text-center text-xs text-gray-500 pt-1">+{students.length - 5} more</p>}
                      </div>
                    )}
                  </div>

                  {/* Recent Teachers */}
                  <div className="bg-white/5 border border-white/10 rounded-3xl p-6">
                    <div className="flex items-center justify-between mb-5">
                      <h3 className="font-bold text-base flex items-center gap-2">
                        <GraduationCap size={16} className="text-purple-400" /> Teachers
                      </h3>
                      <button onClick={() => goToSection('tutors')} className="text-xs text-accentBlue hover:underline flex items-center gap-1">
                        View all <ArrowRight size={12} />
                      </button>
                    </div>
                    {tutors.length === 0 ? (
                      <p className="text-gray-500 text-sm text-center py-8">No teachers registered yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {tutors.slice(0, 4).map((t) => (
                          <div key={t._id} className="flex items-center gap-3 p-3 bg-darkBg rounded-2xl border border-white/5 hover:border-white/10 transition-colors">
                            <div className="w-9 h-9 rounded-full bg-purple-500/10 text-purple-400 flex items-center justify-center text-sm font-bold shrink-0">
                              {(t.firstName?.[0] || t.name?.[0] || '?').toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm text-white truncate">
                                {t.firstName ? `${t.firstName} ${t.lastName}` : t.name}
                              </p>
                              <p className="text-xs text-gray-400 truncate">{t.subjects?.map((s) => s.subjectName).join(', ') || '—'}</p>
                            </div>
                            <div className="flex gap-1.5 shrink-0">
                              <span className="text-[10px] px-2 py-0.5 bg-white/10 text-gray-300 rounded-full">{t.mode}</span>
                              {(t.syllabus || []).slice(0, 1).map((sy) => (
                                <span key={sy} className="text-[10px] px-2 py-0.5 bg-purple-500/10 text-purple-400 rounded-full">{sy}</span>
                              ))}
                            </div>
                          </div>
                        ))}
                        {tutors.length > 4 && <p className="text-center text-xs text-gray-500 pt-1">+{tutors.length - 4} more</p>}
                      </div>
                    )}
                  </div>
                </div>

                {/* Right 1/3 */}
                <div className="space-y-6">

                  {/* Quick Actions */}
                  <div className="bg-white/5 border border-white/10 rounded-3xl p-6">
                    <h3 className="font-bold text-base mb-5">Quick Actions</h3>
                    <div className="space-y-2">
                      {[
                        { label: 'Register Student', key: 'students', icon: UserPlus,     color: 'text-cyan-400'    },
                        { label: 'Register Teacher', key: 'tutors',   icon: GraduationCap, color: 'text-purple-400' },
                        { label: 'Add Product',      key: 'products', icon: ShoppingBag,  color: 'text-blue-400'    },
                        { label: 'Add Subject',      key: 'subjects', icon: BookOpen,     color: 'text-orange-400'  },
                        { label: 'Post Announcement', key: 'ads',      icon: Megaphone,    color: 'text-yellow-400'  },
                      ].map((action) => (
                        <button key={action.key} onClick={() => goToSection(action.key)}
                          className="w-full flex items-center gap-3 px-4 py-3 bg-darkBg border border-white/5 rounded-xl hover:border-white/20 hover:bg-white/5 transition-all group text-left">
                          <action.icon size={15} className={`${action.color} shrink-0 group-hover:scale-110 transition-transform`} />
                          <span className="text-sm text-gray-300 group-hover:text-white transition-colors flex-1">{action.label}</span>
                          <ArrowRight size={13} className="text-gray-600 group-hover:text-gray-300 transition-colors" />
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Announcements */}
                  <div className="bg-white/5 border border-white/10 rounded-3xl p-6">
                    <div className="flex items-center justify-between mb-5">
                      <h3 className="font-bold text-base flex items-center gap-2">
                        <Megaphone size={16} className="text-yellow-400" /> Announcements
                      </h3>
                      <button onClick={() => goToSection('ads')} className="text-xs text-accentBlue hover:underline">Manage</button>
                    </div>
                    {ads.length === 0 ? (
                      <p className="text-gray-500 text-sm text-center py-4">No announcements yet.</p>
                    ) : (
                      <div className="space-y-3">
                        {ads.slice(0, 3).map((ad) => (
                          <div key={ad._id} className="flex gap-3 items-start p-3 bg-darkBg rounded-2xl border border-white/5">
                            {ad.imageUrl && <img src={ad.imageUrl} alt="" className="w-11 h-11 rounded-xl object-cover shrink-0" />}
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-white truncate">{ad.title}</p>
                              <p className="text-xs text-gray-400 line-clamp-1">{ad.description || ad.text}</p>
                            </div>
                          </div>
                        ))}
                        {ads.length > 3 && <p className="text-center text-xs text-gray-500">+{ads.length - 3} more</p>}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Recent Products */}
              {products.length > 0 && (
                <div className="bg-white/5 border border-white/10 rounded-3xl p-6">
                  <div className="flex items-center justify-between mb-5">
                    <h3 className="font-bold text-base flex items-center gap-2">
                      <ShoppingBag size={16} className="text-blue-400" /> Recent Products
                    </h3>
                    <button onClick={() => goToSection('products')} className="text-xs text-accentBlue hover:underline flex items-center gap-1">
                      View all <ArrowRight size={12} />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {products.slice(0, 4).map((p) => (
                      <div key={p._id} onClick={() => goToSection('products')}
                        className="bg-darkBg border border-white/5 rounded-2xl overflow-hidden hover:border-white/15 transition-all group cursor-pointer">
                        {p.imageUrl ? (
                          <img src={p.imageUrl} alt={p.title} className="w-full h-28 object-cover group-hover:scale-105 transition-transform duration-300" />
                        ) : (
                          <div className="w-full h-28 bg-white/5 flex items-center justify-center">
                            <ShoppingBag size={22} className="text-gray-600" />
                          </div>
                        )}
                        <div className="p-3">
                          <p className="text-xs font-semibold text-white truncate">{p.title}</p>
                          <p className="text-accentBlue text-xs font-bold mt-1">Rs. {Number(p.price).toLocaleString()}</p>
                          <div className="flex gap-1 mt-1.5">
                            <span className="text-[9px] px-1.5 py-0.5 bg-white/10 text-gray-400 rounded-full">{p.syllabus}</span>
                            <span className="text-[9px] px-1.5 py-0.5 bg-white/10 text-gray-400 rounded-full">{p.level}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>
          )}

          {/* ══════════════ SHOP / PRODUCTS ══════════════ */}
          {activeSection === 'products' && (
            <>
              {/* Form */}
              <div className="bg-white/5 border border-white/10 rounded-3xl p-6 mb-8">
                <h3 className="text-xl font-bold mb-1 flex items-center gap-2">
                  {editingProductId ? <Pencil size={18} className="text-accentBlue" /> : <Plus size={18} className="text-accentBlue" />}
                  {editingProductId ? 'Edit Product' : 'Add New Product'}
                </h3>
                <p className="text-gray-400 text-sm mb-6">
                  {editingProductId ? 'Update the product details.' : 'Add a book or material to the shop.'}
                </p>
                <form onSubmit={handleProductSubmit} className="space-y-4">
                  {/* Title + Price + Stock */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-1">
                      <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Title</label>
                      <input value={productForm.title} onChange={e => setProductForm(p => ({ ...p, title: e.target.value }))}
                        placeholder="e.g. Combined Maths Past Paper Book"
                        className="w-full bg-darkBg border border-white/10 p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Price (Rs.)</label>
                      <input type="number" min="0" value={productForm.price} onChange={e => setProductForm(p => ({ ...p, price: e.target.value }))}
                        placeholder="1500"
                        className="w-full bg-darkBg border border-white/10 p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Stock</label>
                      <input type="number" min="0" value={productForm.stock} onChange={e => setProductForm(p => ({ ...p, stock: e.target.value }))}
                        placeholder="0"
                        className="w-full bg-darkBg border border-white/10 p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all" />
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Description</label>
                    <textarea value={productForm.description} onChange={e => setProductForm(p => ({ ...p, description: e.target.value }))}
                      placeholder="Brief description of the product..."
                      rows={3}
                      className="w-full bg-darkBg border border-white/10 p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all resize-none" />
                  </div>

                  {/* Syllabus + Subject + Level */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Syllabus</label>
                      <select value={productForm.syllabus} onChange={e => setProductForm(p => ({ ...p, syllabus: e.target.value }))}
                        className="w-full bg-darkBg border border-white/10 p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all">
                        <option value="Local">Local</option>
                        <option value="Cambridge">Cambridge</option>
                        <option value="Edexcel">Edexcel</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Subject</label>
                      <input value={productForm.subject} onChange={e => setProductForm(p => ({ ...p, subject: e.target.value }))}
                        placeholder="e.g. Physics"
                        className="w-full bg-darkBg border border-white/10 p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Level</label>
                      <select value={productForm.level} onChange={e => setProductForm(p => ({ ...p, level: e.target.value }))}
                        className="w-full bg-darkBg border border-white/10 p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all">
                        <option value="O/L">O/L</option>
                        <option value="A/L">A/L</option>
                        <option value="IGCSE">IGCSE</option>
                        <option value="AS/A-Level">AS/A-Level</option>
                        <option value="IAL">IAL</option>
                      </select>
                    </div>
                  </div>

                  {/* Image + Preview */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Product Image (max 2MB)</label>
                      <input type="file" accept="image/*" onChange={handleProductImageSelect}
                        className="w-full bg-darkBg border border-white/10 p-3 rounded-xl text-sm outline-none file:mr-4 file:border-0 file:bg-accentBlue/10 file:text-accentBlue file:px-4 file:py-1.5 file:rounded-lg file:font-semibold hover:file:bg-accentBlue/20 file:transition-colors file:cursor-pointer cursor-pointer" />
                    </div>
                    {productImageUrl ? (
                      <img src={productImageUrl} alt="Preview" className="w-full h-36 object-cover rounded-xl border border-white/10" />
                    ) : (
                      <div className="w-full h-36 rounded-xl border border-dashed border-white/20 flex items-center justify-center text-xs text-gray-400">Image preview</div>
                    )}
                  </div>

                  {/* Available toggle */}
                  <label className="flex items-center gap-3 cursor-pointer w-fit">
                    <input type="checkbox" checked={productForm.isAvailable}
                      onChange={e => setProductForm(p => ({ ...p, isAvailable: e.target.checked }))}
                      className="accent-accentBlue w-4 h-4" />
                    <span className="text-sm text-gray-300">Available for purchase</span>
                  </label>

                  {productFeedback.message && (
                    <p className={`text-sm ${productFeedback.type === 'error' ? 'text-red-400' : 'text-emerald-400'}`}>{productFeedback.message}</p>
                  )}

                  <div className="flex gap-3">
                    <button type="submit" disabled={isSavingProduct}
                      className="bg-accentBlue text-black py-3 px-8 rounded-xl font-bold text-sm flex items-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-50">
                      {editingProductId ? <Pencil size={15} /> : <Plus size={15} />}
                      {isSavingProduct ? 'Saving…' : editingProductId ? 'Update Product' : 'Add Product'}
                    </button>
                    {editingProductId && (
                      <button type="button" onClick={resetProductForm}
                        className="bg-white/10 text-white py-3 px-6 rounded-xl font-bold text-sm hover:bg-white/20 transition-colors">Cancel</button>
                    )}
                  </div>
                </form>
              </div>

              {/* Product list */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                <h3 className="text-xl font-bold">Products <span className="text-sm text-gray-400 font-normal">({products.length})</span></h3>
                <div className="relative w-full sm:w-72">
                  <Search className="absolute left-3 top-2.5 text-gray-500" size={16} />
                  <input type="text" placeholder="Search products…" value={productSearch}
                    onChange={e => setProductSearch(e.target.value)}
                    className="w-full bg-darkBg border border-white/10 py-2.5 pl-10 pr-4 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {products
                  .filter(p => !productSearch || p.title.toLowerCase().includes(productSearch.toLowerCase()) || p.subject.toLowerCase().includes(productSearch.toLowerCase()))
                  .map(p => (
                    <div key={p._id} className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:border-white/20 transition-all flex flex-col">
                      {p.imageUrl ? (
                        <img src={p.imageUrl} alt={p.title} className="w-full h-40 object-cover" />
                      ) : (
                        <div className="w-full h-40 bg-white/5 flex items-center justify-center text-gray-600 text-xs">No image</div>
                      )}
                      <div className="p-4 flex-1 flex flex-col">
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          <span className="text-[10px] px-2 py-0.5 bg-accentBlue/10 text-accentBlue rounded-full font-semibold">{p.syllabus}</span>
                          <span className="text-[10px] px-2 py-0.5 bg-white/10 text-gray-300 rounded-full">{p.level}</span>
                          {!p.isAvailable && <span className="text-[10px] px-2 py-0.5 bg-red-500/10 text-red-400 rounded-full">Unavailable</span>}
                        </div>
                        <p className="font-bold text-white text-sm mb-0.5 line-clamp-2">{p.title}</p>
                        <p className="text-xs text-gray-400 mb-2">{p.subject} · Stock: {p.stock ?? 0}</p>
                        <p className="text-accentBlue font-bold text-base mt-auto mb-3">Rs. {Number(p.price).toLocaleString()}</p>
                        <div className="flex gap-2 border-t border-white/5 pt-3">
                          <button onClick={() => handleEditProduct(p)}
                            className="flex-1 py-2 rounded-xl text-xs font-bold border border-white/10 text-white hover:bg-accentBlue/10 hover:text-accentBlue hover:border-accentBlue/30 transition-colors flex items-center justify-center gap-1">
                            <Pencil size={12} /> Edit
                          </button>
                          <button onClick={() => handleDeleteProduct(p._id)}
                            className="flex-1 py-2 rounded-xl text-xs font-bold border border-white/10 text-white hover:bg-red-500/10 hover:text-red-400 hover:border-red-400/30 transition-colors flex items-center justify-center gap-1">
                            <Trash2 size={12} /> Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                {products.length === 0 && (
                  <div className="md:col-span-2 xl:col-span-3 py-12 text-center bg-white/5 border border-dashed border-white/10 rounded-2xl text-gray-500 text-sm">
                    No products yet. Add the first one above.
                  </div>
                )}
              </div>
            </>
          )}

          {activeSection === 'tutors' && (
            <>
              <div className="bg-white/5 border border-white/10 rounded-3xl p-6 mb-8">
                <h3 className="text-xl font-bold mb-5 flex items-center gap-2">
                  {editingTutorId ? 'Edit Teacher' : 'Register New Teacher'}
                </h3>
                <form onSubmit={handleTutorSubmit} className="space-y-5">
                  {/* Name row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">First Name</label>
                      <input
                        value={tutorForm.firstName}
                        onChange={(e) => handleTutorNameChange('firstName', e.target.value)}
                        placeholder="e.g. Kasun"
                        className="w-full bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Last Name</label>
                      <input
                        value={tutorForm.lastName}
                        onChange={(e) => handleTutorNameChange('lastName', e.target.value)}
                        placeholder="e.g. Perera"
                        className="w-full bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Email</label>
                    <input
                      type="email"
                      value={tutorForm.email}
                      onChange={(e) => setTutorForm((prev) => ({ ...prev, email: e.target.value }))}
                      placeholder="teacher@example.com"
                      className="w-full bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all"
                    />
                  </div>

                  {/* Auto-generated credentials */}
                  <div className="bg-accentBlue/5 border border-accentBlue/20 rounded-2xl p-5 space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-accentBlue font-bold uppercase tracking-wider">Auto-Generated Login Credentials</p>
                      <button type="button" onClick={regenTutorCreds}
                        className="flex items-center gap-1.5 text-xs text-accentBlue hover:text-white bg-accentBlue/10 hover:bg-accentBlue/20 px-3 py-1.5 rounded-lg font-semibold transition-colors">
                        <RefreshCw size={12} /> Regenerate
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Username</label>
                        <input
                          value={tutorForm.username}
                          onChange={(e) => setTutorForm((prev) => ({ ...prev, username: e.target.value }))}
                          placeholder="auto-generated"
                          className="w-full bg-darkBg border border-white/10 p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all font-mono"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">
                          {editingTutorId ? 'New Password (blank = keep current)' : 'Password'}
                        </label>
                        <div className="relative">
                          <input
                            type={showTutorPassword ? 'text' : 'password'}
                            value={tutorForm.password}
                            onChange={(e) => setTutorForm((prev) => ({ ...prev, password: e.target.value }))}
                            placeholder="auto-generated"
                            className="w-full bg-darkBg border border-white/10 p-3 pr-10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all font-mono"
                          />
                          <button type="button" onClick={() => setShowTutorPassword(v => !v)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors">
                            {showTutorPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                          </button>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">Username: first name + numbers (max 10) · Password: last name + numbers + symbol + capital (min 8)</p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-400 mb-2 uppercase tracking-wider">Syllabus</p>
                    <div className="flex gap-6">
                      {['Local', 'Cambridge', 'Edexcel'].map((s) => (
                        <label key={s} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={tutorForm.syllabus.includes(s)}
                            onChange={(e) => {
                              setTutorForm((prev) => ({
                                ...prev,
                                syllabus: e.target.checked
                                  ? [...prev.syllabus, s]
                                  : prev.syllabus.filter((x) => x !== s),
                              }));
                              setSubjectInput((prev) => ({ ...prev, subjectName: '' }));
                            }}
                            className="accent-accentBlue w-4 h-4"
                          />
                          <span className="text-sm">{s}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <select
                      value={tutorForm.mode}
                      onChange={(e) => setTutorForm((prev) => ({ ...prev, mode: e.target.value }))}
                      className="bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all"
                    >
                      <option value="Online">Online</option>
                      <option value="Physical">Physical</option>
                      <option value="Both">Both</option>
                    </select>
                    <input
                      value={tutorForm.area}
                      onChange={(e) => setTutorForm((prev) => ({ ...prev, area: e.target.value }))}
                      placeholder="Area (e.g. Colombo)"
                      className="bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all"
                    />
                  </div>

                  <div>
                    <p className="text-xs text-gray-400 mb-2 uppercase tracking-wider">Subjects</p>
                    <div className="flex gap-2 mb-3">
                      <select
                        value={subjectInput.level}
                        onChange={(e) => setSubjectInput({ level: e.target.value, subjectName: '' })}
                        className="w-28 shrink-0 bg-darkBg border border-white/10 p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all"
                      >
                        <option value="O/L">O/L</option>
                        <option value="A/L">A/L</option>
                      </select>
                      <select
                        value={subjectInput.subjectName}
                        onChange={(e) => setSubjectInput((prev) => ({ ...prev, subjectName: e.target.value }))}
                        className="flex-1 bg-darkBg border border-white/10 p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all"
                      >
                        <option value="">-- Select Subject --</option>
                        {subjects
                          .filter((s) =>
                            s.level === subjectInput.level &&
                            (tutorForm.syllabus.length === 0 || tutorForm.syllabus.includes(s.syllabus))
                          )
                          .map((s) => (
                            <option key={s._id} value={s.name}>{s.name} ({s.syllabus})</option>
                          ))}
                      </select>
                      <button
                        type="button"
                        onClick={handleAddTutorSubject}
                        disabled={!subjectInput.subjectName}
                        className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-xl text-sm font-bold transition-colors flex items-center gap-2 shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Plus size={14} /> Add
                      </button>
                    </div>
                    {tutorSubjects.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {tutorSubjects.map((subj, idx) => (
                          <span key={idx} className="flex items-center gap-2 bg-accentBlue/10 border border-accentBlue/20 text-accentBlue px-3 py-1.5 rounded-full text-xs font-semibold">
                            {subj.subjectName}{subj.levels.length > 0 && ` (${subj.levels.join(', ')})`}
                            <button type="button" onClick={() => setTutorSubjects((prev) => prev.filter((_, i) => i !== idx))} className="text-accentBlue/70 hover:text-red-400 transition-colors ml-1">
                              <XCircle size={13} />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <input
                    value={tutorForm.tags}
                    onChange={(e) => setTutorForm((prev) => ({ ...prev, tags: e.target.value }))}
                    placeholder="Tags (comma separated, e.g. experienced, top-rated)"
                    className="w-full bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all"
                  />

                  <label className="flex items-center gap-3 cursor-pointer w-fit">
                    <input
                      type="checkbox"
                      checked={tutorForm.isPaidAd}
                      onChange={(e) => setTutorForm((prev) => ({ ...prev, isPaidAd: e.target.checked }))}
                      className="accent-accentBlue w-4 h-4"
                    />
                    <span className="text-sm text-gray-300">Mark as Paid Ad</span>
                  </label>

                  {tutorFeedback.message && (
                    <p className={`text-sm ${tutorFeedback.type === 'error' ? 'text-red-400' : 'text-emerald-400'}`}>
                      {tutorFeedback.message}
                    </p>
                  )}

                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={isSavingTutor}
                      className="bg-accentBlue text-black py-3.5 px-8 rounded-xl font-bold text-sm flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {editingTutorId ? <Pencil size={16} /> : <Plus size={16} />}
                      {isSavingTutor ? 'Saving...' : editingTutorId ? 'Update Teacher' : 'Register Teacher'}
                    </button>
                    {editingTutorId && (
                      <button type="button" onClick={handleCancelEditTutor} className="bg-white/10 text-white py-3.5 px-8 rounded-xl font-bold text-sm hover:bg-white/20 transition-colors">
                        Cancel
                      </button>
                    )}
                  </div>
                </form>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {tutors.map((item) => (
                  <div key={item._id} className="bg-white/5 border border-white/10 rounded-3xl p-6 hover:bg-white/10 transition-colors group flex flex-col">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 rounded-full bg-accentBlue/20 text-accentBlue flex items-center justify-center text-xl font-bold shrink-0">
                        {(item.firstName || item.name || '?').charAt(0)}
                      </div>
                      <div>
                        <h3 className="font-bold text-lg">{item.firstName ? `${item.firstName} ${item.lastName}` : item.name}</h3>
                        {item.username && <p className="text-xs text-gray-500 font-mono">@{item.username}</p>}
                        <p className="text-sm text-accentBlue">{item.subjects?.map((s) => s.subjectName).join(', ') || '—'}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs mb-4">
                      <span className="px-3 py-1 bg-white/10 rounded-full text-gray-300">{item.mode}</span>
                      {item.area && <span className="px-3 py-1 bg-white/10 rounded-full text-gray-300">{item.area}</span>}
                      {item.syllabus?.map((s) => (
                        <span key={s} className="px-3 py-1 bg-accentBlue/10 rounded-full text-accentBlue">{s}</span>
                      ))}
                      {item.isPaidAd && <span className="px-3 py-1 bg-yellow-400/10 rounded-full text-yellow-400">Paid Ad</span>}
                    </div>
                    <div className="flex gap-2 pt-4 border-t border-white/5 mt-auto">
                      <button type="button" onClick={() => handleEditTutor(item)} className="flex-1 py-2 rounded-xl text-xs font-bold border border-white/10 text-white hover:bg-accentBlue/10 hover:text-accentBlue hover:border-accentBlue/30 transition-colors">Edit</button>
                      <button type="button" onClick={() => handleDeleteTutor(item._id)} className="flex-1 py-2 rounded-xl text-xs font-bold border border-white/10 text-white hover:bg-red-500/10 hover:text-red-400 hover:border-red-400/30 transition-colors">Delete</button>
                    </div>
                  </div>
                ))}
                {tutors.length === 0 && (
                  <div className="md:col-span-2 lg:col-span-3 text-center p-12 bg-white/5 border border-dashed border-white/20 rounded-3xl text-gray-500 text-sm">
                    No teachers registered yet.
                  </div>
                )}
              </div>
            </>
          )}

          {/* ══════════════ STUDENTS ══════════════ */}
          {activeSection === 'students' && (
            <>
              {/* Registration form */}
              <div className="bg-white/5 border border-white/10 rounded-3xl p-6 mb-8">
                <h3 className="text-xl font-bold mb-1 flex items-center gap-2">
                  {editingStudentId ? <Pencil size={20} className="text-accentBlue" /> : <UserPlus size={20} className="text-accentBlue" />}
                  {editingStudentId ? 'Edit Student' : 'Register New Student'}
                </h3>
                <p className="text-gray-400 text-sm mb-6">
                  {editingStudentId ? 'Update the student\'s details. Leave password blank to keep the current one.' : 'Admin registers students. Credentials are auto-generated and can be edited.'}
                </p>
                <form onSubmit={handleRegisterStudent} className="space-y-5">

                  {/* Name row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">First Name</label>
                      <input value={studentForm.firstName}
                        onChange={e => handleStudentNameChange('firstName', e.target.value)}
                        placeholder="e.g. Kasun"
                        className="w-full bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Last Name</label>
                      <input value={studentForm.lastName}
                        onChange={e => handleStudentNameChange('lastName', e.target.value)}
                        placeholder="e.g. Perera"
                        className="w-full bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all" />
                    </div>
                  </div>

                  {/* Email / Phone / Age */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Email</label>
                      <input type="email" value={studentForm.email}
                        onChange={e => setStudentForm(p => ({ ...p, email: e.target.value }))}
                        placeholder="student@example.com"
                        className="w-full bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Phone</label>
                      <input type="tel" value={studentForm.phone}
                        onChange={e => setStudentForm(p => ({ ...p, phone: e.target.value }))}
                        placeholder="07X XXX XXXX"
                        className="w-full bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Age</label>
                      <input type="number" min="5" max="30" value={studentForm.age}
                        onChange={e => setStudentForm(p => ({ ...p, age: e.target.value }))}
                        placeholder="16"
                        className="w-full bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all" />
                    </div>
                  </div>

                  {/* Level */}
                  <div>
                    <label className="block text-xs text-gray-400 mb-2 uppercase tracking-wider">Level</label>
                    <div className="flex gap-4">
                      {['O/L', 'A/L'].map(lv => (
                        <label key={lv} className="flex items-center gap-2 cursor-pointer">
                          <input type="radio" name="studentLevel" value={lv}
                            checked={studentForm.level === lv}
                            onChange={() => { setStudentForm(p => ({ ...p, level: lv })); setStudentSubjectInput(''); }}
                            className="accent-accentBlue w-4 h-4" />
                          <span className="text-sm font-semibold">{lv}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Syllabus */}
                  <div>
                    <label className="block text-xs text-gray-400 mb-2 uppercase tracking-wider">Syllabus</label>
                    <div className="flex gap-6">
                      {['Local', 'Cambridge', 'Edexcel'].map(sy => (
                        <label key={sy} className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox"
                            checked={studentForm.syllabus.includes(sy)}
                            onChange={e => {
                              setStudentForm(p => ({
                                ...p,
                                syllabus: e.target.checked ? [...p.syllabus, sy] : p.syllabus.filter(x => x !== sy),
                              }));
                              setStudentSubjectInput('');
                            }}
                            className="accent-accentBlue w-4 h-4" />
                          <span className="text-sm">{sy}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Subjects */}
                  <div>
                    <label className="block text-xs text-gray-400 mb-2 uppercase tracking-wider">Subjects</label>
                    <div className="flex gap-2 mb-3">
                      <select value={studentSubjectInput}
                        onChange={e => setStudentSubjectInput(e.target.value)}
                        className="flex-1 bg-darkBg border border-white/10 p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all">
                        <option value="">-- Select Subject --</option>
                        {subjects
                          .filter(s => s.level === studentForm.level && (studentForm.syllabus.length === 0 || studentForm.syllabus.includes(s.syllabus)))
                          .map(s => <option key={s._id} value={s.name}>{s.name} ({s.syllabus})</option>)}
                      </select>
                      <button type="button" onClick={handleAddStudentSubject} disabled={!studentSubjectInput}
                        className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-xl text-sm font-bold transition-colors flex items-center gap-2 shrink-0 disabled:opacity-40 disabled:cursor-not-allowed">
                        <Plus size={14} /> Add
                      </button>
                    </div>
                    {studentSubjects.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {studentSubjects.map((s, i) => (
                          <span key={i} className="flex items-center gap-2 bg-accentBlue/10 border border-accentBlue/20 text-accentBlue px-3 py-1.5 rounded-full text-xs font-semibold">
                            {s.subjectName}
                            <button type="button" onClick={() => setStudentSubjects(prev => prev.filter((_, idx) => idx !== i))} className="hover:text-red-400 transition-colors"><XCircle size={13} /></button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Auto-generated credentials */}
                  <div className="bg-accentBlue/5 border border-accentBlue/20 rounded-2xl p-5 space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-accentBlue font-bold uppercase tracking-wider">Auto-Generated Login Credentials</p>
                      <button type="button" onClick={() => regenStudentCreds()}
                        className="flex items-center gap-1.5 text-xs text-accentBlue hover:text-white transition-colors bg-accentBlue/10 hover:bg-accentBlue/20 px-3 py-1.5 rounded-lg font-semibold">
                        <RefreshCw size={12} /> Regenerate
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Username</label>
                        <input value={studentForm.username}
                          onChange={e => setStudentForm(p => ({ ...p, username: e.target.value }))}
                          placeholder="auto-generated"
                          className="w-full bg-darkBg border border-white/10 p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all font-mono" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Password</label>
                        <div className="relative">
                          <input type={showStudentPassword ? 'text' : 'password'}
                            value={studentForm.password}
                            onChange={e => setStudentForm(p => ({ ...p, password: e.target.value }))}
                            placeholder="auto-generated"
                            className="w-full bg-darkBg border border-white/10 p-3 pr-10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all font-mono" />
                          <button type="button" onClick={() => setShowStudentPassword(v => !v)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors">
                            {showStudentPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                          </button>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">Username: first name + numbers (max 10 chars) · Password: last name + numbers + symbol + capital (min 8 chars)</p>
                  </div>

                  {studentFeedback.message && (
                    <p className={`text-sm flex items-center gap-1.5 ${studentFeedback.type === 'error' ? 'text-red-400' : 'text-emerald-400'}`}>
                      {studentFeedback.type === 'error' ? <XCircle size={14} /> : <CheckCircle size={14} />}
                      {studentFeedback.message}
                    </p>
                  )}

                  <div className="flex gap-3">
                    <button type="submit" disabled={isSavingStudent}
                      className="bg-accentBlue text-black py-3.5 px-8 rounded-xl font-bold text-sm flex items-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:cursor-not-allowed">
                      {editingStudentId ? <Pencil size={16} /> : <UserPlus size={16} />}
                      {isSavingStudent ? 'Saving…' : editingStudentId ? 'Update Student' : 'Register Student'}
                    </button>
                    <button type="button" onClick={resetStudentForm}
                      className="bg-white/10 text-white py-3.5 px-6 rounded-xl font-bold text-sm hover:bg-white/20 transition-colors">
                      {editingStudentId ? 'Cancel' : 'Clear'}
                    </button>
                  </div>
                </form>
              </div>

              {/* Student list */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                <h3 className="text-xl font-bold">Registered Students
                  <span className="ml-2 text-sm font-normal text-gray-400">({students.length})</span>
                </h3>
                <div className="relative w-full sm:w-72">
                  <Search className="absolute left-3 top-2.5 text-gray-500" size={16} />
                  <input type="text" placeholder="Search students…" value={studentSearch}
                    onChange={e => setStudentSearch(e.target.value)}
                    className="w-full bg-darkBg border border-white/10 py-2.5 pl-10 pr-4 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all" />
                </div>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-3xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-white/5 text-gray-400 text-xs uppercase tracking-wider">
                      <tr>
                        <th className="px-5 py-4 font-medium">Student</th>
                        <th className="px-5 py-4 font-medium">Username</th>
                        <th className="px-5 py-4 font-medium">Level</th>
                        <th className="px-5 py-4 font-medium">Syllabus</th>
                        <th className="px-5 py-4 font-medium">Subjects</th>
                        <th className="px-5 py-4 font-medium text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {students
                        .filter(st => {
                          const q = studentSearch.toLowerCase();
                          return !q || `${st.firstName} ${st.lastName} ${st.email} ${st.username}`.toLowerCase().includes(q);
                        })
                        .map(st => (
                          <tr key={st._id} className="hover:bg-white/5 transition-colors">
                            <td className="px-5 py-4">
                              <p className="font-semibold text-white">{st.firstName} {st.lastName}</p>
                              <p className="text-xs text-gray-400">{st.email}</p>
                              {st.phone && <p className="text-xs text-gray-500">{st.phone}</p>}
                            </td>
                            <td className="px-5 py-4 font-mono text-accentBlue text-xs">{st.username || '—'}</td>
                            <td className="px-5 py-4">
                              {st.level && <span className="px-2.5 py-1 bg-purple-500/10 text-purple-400 rounded-full text-xs font-bold">{st.level}</span>}
                            </td>
                            <td className="px-5 py-4">
                              <div className="flex flex-wrap gap-1">
                                {(st.syllabus || []).map(sy => (
                                  <span key={sy} className="px-2 py-0.5 bg-white/10 text-gray-300 rounded-full text-xs">{sy}</span>
                                ))}
                              </div>
                            </td>
                            <td className="px-5 py-4">
                              <div className="flex flex-wrap gap-1">
                                {(st.subjects || []).map((s, i) => (
                                  <span key={i} className="px-2 py-0.5 bg-accentBlue/10 text-accentBlue rounded-full text-xs">{s.subjectName}</span>
                                ))}
                              </div>
                            </td>
                            <td className="px-5 py-4 text-right">
                              <button onClick={() => handleEditStudent(st)}
                                className="text-accentBlue bg-accentBlue/10 hover:bg-accentBlue/20 p-2.5 rounded-lg inline-flex items-center justify-center transition-colors mr-2" title="Edit">
                                <Pencil size={15} />
                              </button>
                              <button onClick={() => handleDeleteStudent(st._id)}
                                className="text-red-400 bg-red-400/10 hover:bg-red-400/20 p-2.5 rounded-lg inline-flex items-center justify-center transition-colors" title="Delete">
                                <Trash2 size={15} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      {students.length === 0 && (
                        <tr><td colSpan="6" className="py-10 text-center text-gray-500">No students registered yet.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {activeSection === 'resources' && (
            <>
              <div className="bg-white/5 border border-white/10 rounded-3xl p-6 mb-8">
                <h3 className="text-xl font-bold mb-5 flex items-center gap-2">Upload Resource</h3>
                <form onSubmit={handleAddResource} className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <input
                    value={resourceForm.title}
                    onChange={(e) => setResourceForm((prev) => ({ ...prev, title: e.target.value }))}
                    placeholder="Resource title"
                    className="bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all md:col-span-2"
                  />
                  <select
                    value={resourceForm.category}
                    onChange={(e) => setResourceForm((prev) => ({ ...prev, category: e.target.value }))}
                    className="bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all"
                  >
                    {RESOURCE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <select
                    value={resourceForm.syllabusType}
                    onChange={(e) => setResourceForm((prev) => ({ ...prev, syllabusType: e.target.value, subject: '' }))}
                    className="bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all"
                  >
                    <option value="Local">Local</option>
                    <option value="Cambridge">Cambridge</option>
                    <option value="Edexcel">Edexcel</option>
                  </select>

                  <select
                    value={resourceForm.subject}
                    onChange={(e) => setResourceForm((prev) => ({ ...prev, subject: e.target.value }))}
                    className="bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all"
                  >
                    <option value="">Select Subject</option>
                    {resourceSubjectOptions.map((s) => <option key={s._id} value={s.name}>{s.name}</option>)}
                  </select>
                  <input
                    value={resourceForm.year}
                    onChange={(e) => setResourceForm((prev) => ({ ...prev, year: e.target.value }))}
                    placeholder="Year (optional)"
                    className="bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all"
                  />
                  {resourceForm.category !== 'Videos' ? (
                    <select
                      value={resourceForm.medium}
                      onChange={(e) => setResourceForm((prev) => ({ ...prev, medium: e.target.value }))}
                      className="bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all"
                    >
                      <option value="">Medium (optional)</option>
                      <option value="Sinhala">Sinhala</option>
                      <option value="English">English</option>
                      <option value="Tamil">Tamil</option>
                    </select>
                  ) : (
                    <input
                      value={resourceForm.videoUrl}
                      onChange={(e) => setResourceForm((prev) => ({ ...prev, videoUrl: e.target.value }))}
                      placeholder="Video URL (YouTube/Vimeo)"
                      className="bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all"
                    />
                  )}

                  {resourceForm.category !== 'Videos' && (
                    <label className="flex items-center gap-2 bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm cursor-pointer hover:border-accentBlue/50 transition-all md:col-span-2">
                      <Upload size={16} className="text-accentBlue shrink-0" />
                      <span className="truncate text-gray-300">{resourceFile ? resourceFile.name : 'Choose PDF file'}</span>
                      <input
                        type="file"
                        accept="application/pdf"
                        onChange={(e) => setResourceFile(e.target.files?.[0] || null)}
                        className="hidden"
                      />
                    </label>
                  )}
                  <label className="flex items-center gap-2 text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={resourceForm.isPremium}
                      onChange={(e) => setResourceForm((prev) => ({ ...prev, isPremium: e.target.checked }))}
                      className="w-4 h-4 accent-accentBlue"
                    />
                    Premium content
                  </label>

                  <button
                    type="submit"
                    disabled={isUploadingResource}
                    className="bg-accentBlue text-black rounded-xl font-bold text-sm flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-50"
                  >
                    <Plus size={16} /> {isUploadingResource ? 'Uploading...' : 'Add Resource'}
                  </button>

                  {resourceFeedback.message && (
                    <p className={`md:col-span-4 text-sm font-medium ${resourceFeedback.type === 'error' ? 'text-red-400' : 'text-green-400'}`}>
                      {resourceFeedback.message}
                    </p>
                  )}
                </form>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-3xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-white/5 text-gray-400 text-xs uppercase tracking-wider">
                      <tr>
                        <th className="px-6 py-4 font-medium">Title</th>
                        <th className="px-6 py-4 font-medium">Category</th>
                        <th className="px-6 py-4 font-medium">Syllabus</th>
                        <th className="px-6 py-4 font-medium">Subject</th>
                        <th className="px-6 py-4 font-medium">Uploaded By</th>
                        <th className="px-6 py-4 font-medium">File</th>
                        <th className="px-6 py-4 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {loadingResources ? (
                      <tr><td colSpan="7" className="py-10 text-center text-gray-500">Loading resources…</td></tr>
                    ) : resources.length === 0 ? (
                      <tr><td colSpan="7" className="py-10 text-center text-gray-500">No resources uploaded yet.</td></tr>
                    ) : resources.map((item) => (
                      <tr key={item._id} className="hover:bg-white/5 transition-colors">
                        <td className="px-6 py-4 font-medium">{item.title}</td>
                        <td className="px-6 py-4 text-gray-400">{item.category}</td>
                        <td className="px-6 py-4 text-gray-400">
                          <span className="bg-white/10 px-3 py-1 rounded-full text-xs">{item.syllabusType}</span>
                        </td>
                        <td className="px-6 py-4 text-gray-400">{item.subject}</td>
                        <td className="px-6 py-4 text-gray-400">
                          {item.uploadedBy === 'teacher'
                            ? <span className="bg-purple-500/10 text-purple-400 px-3 py-1 rounded-full text-xs">{item.tutorName || 'Teacher'}</span>
                            : <span className="bg-accentBlue/10 text-accentBlue px-3 py-1 rounded-full text-xs">Admin</span>}
                        </td>
                        <td className="px-6 py-4">
                          {item.fileUrl ? (
                            <div className="flex items-center gap-3">
                              <button
                                type="button"
                                onClick={() => setPreviewResource(item)}
                                className="flex items-center gap-1.5 text-xs font-semibold text-accentBlue hover:underline"
                              >
                                <Eye size={14} /> View PDF
                              </button>
                              <a
                                href={getResourceFileUrl(item._id, true)}
                                className="flex items-center gap-1.5 text-xs font-semibold text-gray-400 transition-colors hover:text-white"
                              >
                                <Download size={14} /> Download
                              </a>
                              <label className="flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-amber-400 transition-colors hover:text-amber-300">
                                <Upload size={14} />
                                {replacingResourceId === item._id ? 'Replacing…' : 'Replace'}
                                <input
                                  type="file"
                                  accept="application/pdf"
                                  disabled={replacingResourceId === item._id}
                                  className="hidden"
                                  onChange={(event) => {
                                    const file = event.target.files?.[0];
                                    event.target.value = '';
                                    handleReplaceResourceFile(item._id, file);
                                  }}
                                />
                              </label>
                            </div>
                          ) : item.videoUrl ? (
                            <a href={item.videoUrl} target="_blank" rel="noopener noreferrer" className="text-accentBlue hover:underline text-xs font-semibold">Video Link</a>
                          ) : '—'}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button onClick={() => handleDeleteResource(item._id, item.title)} className="text-red-400 hover:text-red-300 transition-colors">
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
              <PdfViewerModal resource={previewResource} onClose={() => setPreviewResource(null)} />
            </>
          )}

          {activeSection === 'subjects' && (
            <>
              <div className="bg-white/5 border border-white/10 rounded-3xl p-6 mb-8">
                <h3 className="text-xl font-bold mb-5 flex items-center gap-2">
                  {editingSubjectId ? 'Update Subject' : 'Add New Subject'}
                </h3>
                <form onSubmit={handleSubjectSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <input
                    value={subjectForm.name}
                    onChange={(e) => setSubjectForm((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="Subject name (e.g. Physics (0625))"
                    className="bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all"
                  />
                  <select
                    value={subjectForm.syllabus}
                    onChange={(e) => setSubjectForm((prev) => ({ ...prev, syllabus: e.target.value }))}
                    className="bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all"
                  >
                    <option value="Local">Local</option>
                    <option value="Cambridge">Cambridge</option>
                    <option value="Edexcel">Edexcel</option>
                  </select>
                  <select
                    value={subjectForm.level}
                    onChange={(e) => setSubjectForm((prev) => ({ ...prev, level: e.target.value }))}
                    className="bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all"
                  >
                    <option value="O/L">O/L</option>
                    <option value="A/L">A/L</option>
                  </select>
                  <div className="flex items-center gap-2">
                    <button type="submit" className="w-full bg-accentBlue text-black rounded-xl font-bold text-sm flex items-center justify-center gap-2 py-3.5 hover:scale-[1.02] transition-transform">
                      {editingSubjectId ? <Pencil size={16} /> : <Plus size={16} />}
                      {editingSubjectId ? 'Update' : 'Add'}
                    </button>
                    {editingSubjectId && (
                      <button type="button" onClick={handleCancelEditSubject} className="w-full bg-white/10 text-white rounded-xl font-bold text-sm py-3.5 hover:bg-white/20 transition-colors">
                        Cancel
                      </button>
                    )}
                  </div>
                </form>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-3xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-white/5 text-gray-400 text-xs uppercase tracking-wider">
                      <tr>
                        <th className="px-6 py-4 font-medium">Name</th>
                        <th className="px-6 py-4 font-medium">Syllabus</th>
                        <th className="px-6 py-4 font-medium">Level</th>
                        <th className="px-6 py-4 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {filteredSubjects.map((item) => (
                      <tr key={item._id} className="hover:bg-white/5 transition-colors">
                        <td className="px-6 py-4 font-medium">{item.name}</td>
                        <td className="px-6 py-4 text-gray-400">
                           <span className="bg-white/10 px-3 py-1 rounded-full text-xs">{item.syllabus}</span>
                        </td>
                        <td className="px-6 py-4 text-gray-400">{item.level}</td>
                        <td className="px-6 py-4 text-right">
                          <button onClick={() => handleEditSubject(item)} className="text-accentBlue bg-accentBlue/10 hover:bg-accentBlue/20 p-2.5 rounded-lg inline-flex items-center justify-center mr-2 transition-colors" title="Edit">
                            <Pencil size={16} />
                          </button>
                          <button onClick={() => handleDeleteSubject(item._id)} className="text-red-400 bg-red-400/10 hover:bg-red-400/20 p-2.5 rounded-lg inline-flex items-center justify-center transition-colors" title="Delete">
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {filteredSubjects.length === 0 && (
                      <tr>
                        <td colSpan="4" className="py-8 text-center text-gray-500">{subjects.length === 0 ? 'No subjects added yet.' : 'No subjects found matching your search.'}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                </div>
              </div>
            </>
          )}

          {/* ══════════════ BLOGS ══════════════ */}
          {activeSection === 'blogs' && (
            <>
              <div className="bg-white/5 border border-white/10 rounded-3xl p-6 mb-8">
                <h3 className="text-xl font-bold mb-1 flex items-center gap-2">
                  {editingBlogId ? <Pencil size={18} className="text-accentBlue" /> : <Plus size={18} className="text-accentBlue" />}
                  {editingBlogId ? 'Edit Post' : 'Write New Post'}
                </h3>
                <p className="text-gray-400 text-sm mb-6">{editingBlogId ? 'Update the blog post.' : 'Create and publish a new blog post.'}</p>

                <form onSubmit={handleBlogSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Title</label>
                      <input value={blogForm.title} onChange={e => setBlogForm(p => ({ ...p, title: e.target.value }))}
                        placeholder="e.g. How to Master Physics for A/L"
                        className="w-full bg-darkBg border border-white/10 p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Category</label>
                      <select value={blogForm.category} onChange={e => setBlogForm(p => ({ ...p, category: e.target.value }))}
                        className="w-full bg-darkBg border border-white/10 p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all"
                        style={{ backgroundColor: '#0b0e14', color: '#e5e7eb' }}>
                        {BLOG_CATEGORIES.map(c => <option key={c} value={c} style={{ backgroundColor: '#0b0e14', color: '#e5e7eb' }}>{c}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Author</label>
                      <input value={blogForm.author} onChange={e => setBlogForm(p => ({ ...p, author: e.target.value }))}
                        placeholder="Author name"
                        className="w-full bg-darkBg border border-white/10 p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Tags (comma separated)</label>
                      <input value={blogForm.tags} onChange={e => setBlogForm(p => ({ ...p, tags: e.target.value }))}
                        placeholder="Physics, A/L, Exam Tips"
                        className="w-full bg-darkBg border border-white/10 p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Short Description (excerpt)</label>
                    <textarea value={blogForm.description} onChange={e => setBlogForm(p => ({ ...p, description: e.target.value }))}
                      placeholder="Brief summary shown on the blog listing page…" rows={2}
                      className="w-full bg-darkBg border border-white/10 p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all resize-none" />
                  </div>

                  <div>
                    <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Full Content</label>
                    <textarea value={blogForm.content} onChange={e => setBlogForm(p => ({ ...p, content: e.target.value }))}
                      placeholder="Write the full blog content here…" rows={8}
                      className="w-full bg-darkBg border border-white/10 p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all resize-y" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Thumbnail Image (max 2MB)</label>
                      <input type="file" accept="image/*" onChange={handleBlogThumbnail}
                        className="w-full bg-darkBg border border-white/10 p-3 rounded-xl text-sm outline-none file:mr-4 file:border-0 file:bg-accentBlue/10 file:text-accentBlue file:px-4 file:py-1.5 file:rounded-lg file:font-semibold hover:file:bg-accentBlue/20 file:transition-colors file:cursor-pointer cursor-pointer" />
                    </div>
                    {blogThumbnail ? (
                      <img src={blogThumbnail} alt="Preview" className="w-full h-32 object-cover rounded-xl border border-white/10" />
                    ) : (
                      <div className="w-full h-32 rounded-xl border border-dashed border-white/20 flex items-center justify-center text-xs text-gray-500">Thumbnail preview</div>
                    )}
                  </div>

                  {blogFeedback.message && (
                    <p className={`text-sm ${blogFeedback.type === 'error' ? 'text-red-400' : 'text-emerald-400'}`}>{blogFeedback.message}</p>
                  )}

                  <div className="flex gap-3">
                    <button type="submit" disabled={isSavingBlog}
                      className="bg-accentBlue text-black py-3 px-8 rounded-xl font-bold text-sm flex items-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-50">
                      {editingBlogId ? <Pencil size={15} /> : <Plus size={15} />}
                      {isSavingBlog ? 'Saving…' : editingBlogId ? 'Update Post' : 'Publish Post'}
                    </button>
                    {editingBlogId && (
                      <button type="button" onClick={resetBlogForm}
                        className="bg-white/10 text-white py-3 px-6 rounded-xl font-bold text-sm hover:bg-white/20 transition-colors">Cancel</button>
                    )}
                  </div>
                </form>
              </div>

              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold">Published Posts <span className="text-sm text-gray-400 font-normal">({blogs.length})</span></h3>
              </div>

              {blogs.length === 0 ? (
                <div className="py-12 text-center bg-white/5 border border-dashed border-white/10 rounded-2xl text-gray-500 text-sm">No blog posts yet. Write the first one above.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                  {blogs.map(blog => (
                    <div key={blog._id} className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:border-white/20 transition-all flex flex-col">
                      {blog.thumbnail ? (
                        <img src={blog.thumbnail} alt={blog.title} className="w-full h-40 object-cover" />
                      ) : (
                        <div className="w-full h-40 bg-white/5 flex items-center justify-center text-gray-600 text-xs">No thumbnail</div>
                      )}
                      <div className="p-4 flex-1 flex flex-col">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className="text-[10px] font-bold px-2.5 py-0.5 bg-accentBlue/10 text-accentBlue rounded-full">{blog.category}</span>
                          <span className="text-[10px] text-gray-500">{blog.author}</span>
                        </div>
                        <h4 className="font-bold text-white text-sm line-clamp-2 mb-1 flex-1">{blog.title}</h4>
                        <p className="text-xs text-gray-400 line-clamp-2 mb-3">{blog.description}</p>
                        {(blog.tags || []).length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-3">
                            {blog.tags.slice(0, 3).map(t => <span key={t} className="text-[10px] px-2 py-0.5 bg-white/10 text-gray-400 rounded-full">{t}</span>)}
                          </div>
                        )}
                        <div className="flex gap-2 border-t border-white/5 pt-3 mt-auto">
                          <button onClick={() => handleEditBlog(blog)}
                            className="flex-1 py-2 rounded-xl text-xs font-bold border border-white/10 text-white hover:bg-accentBlue/10 hover:text-accentBlue hover:border-accentBlue/30 transition-colors flex items-center justify-center gap-1">
                            <Pencil size={12} /> Edit
                          </button>
                          <button onClick={() => handleDeleteBlog(blog._id)}
                            className="flex-1 py-2 rounded-xl text-xs font-bold border border-white/10 text-white hover:bg-red-500/10 hover:text-red-400 hover:border-red-400/30 transition-colors flex items-center justify-center gap-1">
                            <Trash2 size={12} /> Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {activeSection === 'orders' && (
            <div className="bg-white/5 border border-white/10 rounded-3xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-white/5 text-gray-400 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="px-6 py-4 font-medium">Order ID</th>
                      <th className="px-6 py-4 font-medium">Customer</th>
                      <th className="px-6 py-4 font-medium">Total</th>
                      <th className="px-6 py-4 font-medium">Status</th>
                  </tr>
                </thead>
                  <tbody className="divide-y divide-white/5">
                  {orders.map((item) => (
                      <tr key={item.id} className="hover:bg-white/5 transition-colors">
                        <td className="px-6 py-4 font-medium text-white">{item.id}</td>
                        <td className="px-6 py-4 text-gray-300">{item.customer}</td>
                        <td className="px-6 py-4 text-accentBlue font-bold">{item.total}</td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            item.status === 'Confirmed' ? 'bg-emerald-400/10 text-emerald-400' : 'bg-yellow-400/10 text-yellow-400'
                          }`}>
                            {item.status}
                          </span>
                        </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}

          {/* New Ads Section */}
          {activeSection === 'ads' && (
            <>
              <div className="bg-white/5 border border-white/10 rounded-3xl p-6 mb-8">
                <h3 className="text-xl font-bold mb-1 flex items-center gap-2">
                  <Megaphone size={18} className="text-accentBlue" />
                  {editingAdId ? 'Edit Item' : 'Create News or Announcement'}
                </h3>
                <p className="text-gray-400 text-sm mb-6">Both will appear in the homepage ticker bar.</p>

                <form onSubmit={handleAddAd} className="space-y-5">

                  {/* Type toggle */}
                  <div>
                    <label className="block text-xs text-gray-400 mb-2 uppercase tracking-wider">Type</label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setAdType('announcement')}
                        className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold border transition-all ${
                          adType === 'announcement'
                            ? 'bg-accentBlue/15 border-accentBlue/40 text-accentBlue'
                            : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/20'
                        }`}
                      >
                        <Megaphone size={14} /> Announcement
                      </button>
                      <button
                        type="button"
                        onClick={() => setAdType('news')}
                        className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold border transition-all ${
                          adType === 'news'
                            ? 'bg-amber-500/15 border-amber-500/40 text-amber-400'
                            : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/20'
                        }`}
                      >
                        <FileText size={14} /> News
                      </button>
                    </div>
                  </div>

                  {/* Title */}
                  <div>
                    <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Title</label>
                    <input
                      value={adTitle}
                      onChange={(e) => {
                        const nextValue = e.target.value;
                        if (countWords(nextValue) <= AD_TITLE_WORD_LIMIT) { setAdTitle(nextValue); setAdFeedback({ type: '', message: '' }); }
                        else { setAdTitle(limitWords(nextValue, AD_TITLE_WORD_LIMIT)); setAdFeedback({ type: 'error', message: `Title limited to ${AD_TITLE_WORD_LIMIT} words.` }); }
                      }}
                      placeholder={adType === 'news' ? 'News headline...' : 'Announcement title...'}
                      className="w-full bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Words: <span className={countWords(adTitle) >= AD_TITLE_WORD_LIMIT ? 'text-orange-400' : 'text-gray-400'}>{countWords(adTitle)}</span>/{AD_TITLE_WORD_LIMIT}
                    </p>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Description</label>
                    <textarea
                      value={adDescription}
                      onChange={(e) => {
                        const nextValue = e.target.value;
                        if (countWords(nextValue) <= AD_DESCRIPTION_WORD_LIMIT) { setAdDescription(nextValue); setAdFeedback({ type: '', message: '' }); }
                        else { setAdDescription(limitWords(nextValue, AD_DESCRIPTION_WORD_LIMIT)); setAdFeedback({ type: 'error', message: `Description limited to ${AD_DESCRIPTION_WORD_LIMIT} words.` }); }
                      }}
                      placeholder={adType === 'news' ? 'Brief news summary...' : 'Announcement details...'}
                      rows={3}
                      className="w-full bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all resize-none"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Words: <span className={countWords(adDescription) >= AD_DESCRIPTION_WORD_LIMIT ? 'text-orange-400' : 'text-gray-400'}>{countWords(adDescription)}</span>/{AD_DESCRIPTION_WORD_LIMIT}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">
                        Start Date <span className="normal-case text-gray-500">(blank = show immediately)</span>
                      </label>
                      <input type="date" value={adStartDate} onChange={(e) => setAdStartDate(e.target.value)} onClick={openDatePicker}
                        className="w-full bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">
                        End Date <span className="normal-case text-gray-500">(blank = no expiry)</span>
                      </label>
                      <input type="date" value={adEndDate} onChange={(e) => setAdEndDate(e.target.value)} onClick={openDatePicker}
                        className="w-full bg-darkBg border border-white/10 p-3.5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-accentBlue/20 focus:border-accentBlue transition-all" />
                    </div>
                  </div>

                  {adFeedback.message && (
                    <p className={`text-sm ${adFeedback.type === 'error' ? 'text-red-400' : 'text-emerald-400'}`}>{adFeedback.message}</p>
                  )}

                  <div className="flex gap-3">
                    <button type="submit" disabled={isPostingAd || !adTitle.trim() || !adDescription.trim()}
                      className={`py-3.5 px-8 rounded-xl font-bold text-sm flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:cursor-not-allowed flex-1 md:flex-none ${
                        adType === 'news' ? 'bg-amber-500 text-black' : 'bg-accentBlue text-black'
                      }`}>
                      {editingAdId ? <Pencil size={16} /> : <Plus size={16} />}
                      {isPostingAd ? 'Saving...' : editingAdId ? `Update ${adType === 'news' ? 'News' : 'Announcement'}` : `Publish ${adType === 'news' ? 'News' : 'Announcement'}`}
                    </button>
                    {editingAdId && (
                      <button type="button" onClick={handleCancelEdit}
                        className="bg-white/5 border border-white/15 text-gray-200 py-3.5 px-8 rounded-xl font-bold text-sm hover:bg-white/10 transition-colors">
                        Cancel
                      </button>
                    )}
                  </div>
                </form>
              </div>

              {/* List header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold">Published Items <span className="text-sm text-gray-400 font-normal">({ads.length})</span></h3>
                <div className="flex gap-2 text-xs">
                  <span className="px-3 py-1 rounded-full bg-accentBlue/10 text-accentBlue border border-accentBlue/20 font-semibold">
                    {ads.filter(a => a.type !== 'news').length} Announcements
                  </span>
                  <span className="px-3 py-1 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20 font-semibold">
                    {ads.filter(a => a.type === 'news').length} News
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {ads.map((ad) => (
                  <div key={ad._id} className={`border rounded-3xl overflow-hidden transition-colors flex flex-col ${
                    ad.type === 'news'
                      ? 'bg-amber-500/5 border-amber-500/15 hover:border-amber-500/30'
                      : 'bg-white/5 border-white/10 hover:border-accentBlue/25'
                  }`}>
                    {ad.imageUrl && (
                      <img src={ad.imageUrl} alt="" className="w-full h-40 object-cover border-b border-white/10" />
                    )}
                    <div className="p-5 flex-1 flex flex-col justify-between">
                      <div className="mb-4">
                        <div className="mb-2">
                          <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider ${
                            ad.type === 'news'
                              ? 'bg-amber-500/15 text-amber-400 border border-amber-500/25'
                              : 'bg-accentBlue/10 text-accentBlue border border-accentBlue/20'
                          }`}>
                            {ad.type === 'news' ? <FileText size={9} /> : <Megaphone size={9} />}
                            {ad.type === 'news' ? 'News' : 'Announcement'}
                          </span>
                        </div>
                        <h3 className="font-bold text-lg text-white mb-2 line-clamp-1">{ad.title}</h3>
                        <p className="text-sm text-gray-400 line-clamp-3 leading-relaxed">{ad.description || ad.text}</p>
                        <p className="text-xs text-accentBlue mt-3 font-semibold">
                          {ad.startDate ? `Start: ${new Date(ad.startDate).toLocaleDateString()}` : 'Start: N/A'}
                          {' | '}
                          {ad.endDate ? `End: ${new Date(ad.endDate).toLocaleDateString()}` : 'End: No expiry'}
                        </p>
                      </div>
                      <div className="flex gap-2 pt-4 border-t border-white/5">
                          <button
                            type="button"
                            onClick={() => handleEditAd(ad)}
                            className="flex-1 py-2 rounded-xl text-xs font-bold border border-white/10 text-white hover:bg-accentBlue/10 hover:text-accentBlue hover:border-accentBlue/30 transition-colors"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteAd(ad._id)}
                            className="flex-1 py-2 rounded-xl text-xs font-bold border border-white/10 text-white hover:bg-red-500/10 hover:text-red-400 hover:border-red-400/30 transition-colors"
                          >
                            Delete
                          </button>
                      </div>
                    </div>
                  </div>
                ))}
                
                {ads.length === 0 && (
                  <div className="md:col-span-2 xl:col-span-3 text-center p-12 bg-white/5 border border-dashed border-white/20 rounded-3xl text-gray-500 text-sm">
                    No news or announcements yet. Create one above to display on the homepage ticker.
                  </div>
                )}
              </div>
            </>
          )}

        </section>
      </div>
      </div>
    </div>
  );
};

const ConfirmationModal = ({ isOpen, title, message, onConfirm, onClose }) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: -20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: -20 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="bg-darkBg border border-white/10 rounded-3xl p-8 max-w-md w-full shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="text-center">
            <h3 className="text-xl font-bold mb-4">{title}</h3>
            <p className="text-gray-400 text-sm leading-relaxed">{message}</p>
          </div>
          <div className="flex justify-center gap-4 mt-8">
            <button
              onClick={onClose}
              className="px-6 py-2.5 rounded-xl font-bold text-sm bg-white/10 hover:bg-white/20 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="px-6 py-2.5 rounded-xl font-bold text-sm bg-accentBlue text-black hover:scale-[1.02] transition-transform"
            >
              Confirm
            </button>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

const Toast = ({ isVisible, message, type, onClose }) => {
  const icons = {
    success: <CheckCircle size={20} />,
    error: <XCircle size={20} />,
  };
  const colors = {
    success: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    error: 'bg-red-500/20 text-red-400 border-red-500/30',
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: -50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.9 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          className="fixed top-5 right-5 z-50"
        >
          <div className={`flex items-center gap-3 p-4 rounded-2xl border backdrop-blur-xl shadow-lg ${colors[type]}`}>
            {icons[type]}
            <p className="font-semibold text-sm text-white">{message}</p>
            <button onClick={onClose} className="ml-4 text-white/50 hover:text-white transition-colors">
              <XCircle size={18} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const AdminNavbar = ({ onLogout }) => {
  const navigate = useNavigate();
  const [time, setTime] = useState(new Date());
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ email: '', password: '', confirmPassword: '' });
  const [showProfilePassword, setShowProfilePassword] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState({ type: '', text: '' });
  const [userInfo, setUserInfo] = useState(() => JSON.parse(localStorage.getItem('userInfo') || '{}'));

  // Live clock update every 1 second
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleLogout = () => {
    if (onLogout) onLogout();
    else { localStorage.removeItem('userInfo'); navigate('/'); }
  };

  const closeProfileModal = () => {
    setIsProfileOpen(false);
    setIsEditingProfile(false);
    setProfileMsg({ type: '', text: '' });
  };

  const startEditingProfile = () => {
    setProfileForm({ email: userInfo.email || '', password: '', confirmPassword: '' });
    setProfileMsg({ type: '', text: '' });
    setIsEditingProfile(true);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setProfileMsg({ type: '', text: '' });

    if (profileForm.password && profileForm.password.length < 6) {
      setProfileMsg({ type: 'error', text: 'Password must be at least 6 characters.' });
      return;
    }
    if (profileForm.password && profileForm.password !== profileForm.confirmPassword) {
      setProfileMsg({ type: 'error', text: 'Passwords do not match.' });
      return;
    }

    setProfileSaving(true);
    try {
      const payload = { email: profileForm.email };
      if (profileForm.password) payload.password = profileForm.password;

      const res = await fetch(`${API_BASE_URL}/auth/students/${userInfo._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Update failed.');

      const updatedInfo = { ...userInfo, email: data.email };
      localStorage.setItem('userInfo', JSON.stringify(updatedInfo));
      setUserInfo(updatedInfo);
      setProfileMsg({ type: 'success', text: 'Profile updated successfully.' });
      setIsEditingProfile(false);
    } catch (err) {
      setProfileMsg({ type: 'error', text: err.message });
    } finally {
      setProfileSaving(false);
    }
  };

  return (
    <>
      <nav className="flex items-center justify-between px-[5%] py-4 bg-white/5 backdrop-blur-md border-b border-white/10 sticky top-0 z-50">
      <div 
        className="text-2xl font-bold tracking-tighter text-accentBlue cursor-pointer hover:opacity-80 transition-opacity" 
        onClick={() => navigate('/')}
      >
        SMART EDU NEXT
      </div>
      
      <div className="hidden md:flex flex-col items-center">
        <span className="text-xl font-bold text-white tracking-widest">{time.toLocaleTimeString()}</span>
        <span className="text-xs text-gray-400 uppercase tracking-wider">{time.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
      </div>

      <div className="flex items-center gap-4">
        <button 
          onClick={() => navigate('/')}
          className="bg-white/10 hover:bg-white/20 text-white px-4 py-2.5 rounded-xl text-sm font-bold transition-colors flex items-center gap-2"
        >
          <Home size={16} /> <span className="hidden sm:block">Go to Website</span>
        </button>
        <div
          onClick={() => setIsProfileOpen(true)}
          className="flex items-center gap-3 bg-white/5 px-4 py-2 rounded-xl border border-white/10 cursor-pointer hover:bg-white/10 transition-colors"
        >
          <User size={18} className="text-accentBlue" />
          <span className="text-sm font-semibold text-gray-300 hidden sm:block">
            {userInfo.firstName || 'Admin Profile'}
          </span>
        </div>
        <button onClick={handleLogout} className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm font-bold px-4 py-2.5 rounded-xl hover:bg-red-500/20 transition-colors flex items-center gap-2">
          <LogOut size={16} /> <span className="hidden sm:block">Logout</span>
        </button>
      </div>
      </nav>

      {/* Profile Modal */}
      <AnimatePresence>
        {isProfileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
            onClick={closeProfileModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="bg-darkBg border border-white/10 rounded-[32px] p-10 max-w-lg w-full shadow-2xl relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closeProfileModal}
                className="absolute top-6 right-6 text-gray-400 hover:text-white transition"
              >
                <XCircle size={24} />
              </button>
              <div className="flex flex-col items-center mb-8">
                <div className="w-24 h-24 bg-accentBlue/20 text-accentBlue rounded-full flex items-center justify-center text-4xl font-bold mb-4 border border-accentBlue/30">
                  {(userInfo.firstName?.[0] || 'A').toUpperCase()}
                </div>
                <h2 className="text-3xl font-bold text-white mb-1">
                  {userInfo.firstName || 'Admin'} {userInfo.lastName || ''}
                </h2>
                <span className="px-4 py-1.5 bg-accentBlue/10 text-accentBlue text-xs font-bold rounded-full uppercase tracking-wider mt-2">
                  Administrator
                </span>
              </div>
              
              <div className="space-y-4">
                <div className="bg-white/5 border border-white/10 rounded-2xl p-5 flex items-center gap-4">
                  <div className="p-3 bg-white/5 rounded-xl">
                    <User className="text-accentBlue" size={20} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Full Name</p>
                    <p className="font-semibold text-lg">{userInfo.firstName || 'System'} {userInfo.lastName || 'Admin'}</p>
                  </div>
                </div>
                {!isEditingProfile ? (
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-5 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-white/5 rounded-xl">
                        <Mail className="text-accentBlue" size={20} />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 mb-0.5">Email Address</p>
                        <p className="font-semibold text-lg">{userInfo.email || 'admin@edu-platform.com'}</p>
                      </div>
                    </div>
                    <button
                      onClick={startEditingProfile}
                      title="Edit email & password"
                      className="p-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-accentBlue transition-colors"
                    >
                      <Pencil size={16} />
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSaveProfile} className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Email Address</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
                        <input
                          type="email"
                          required
                          value={profileForm.email}
                          onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                          className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-3 py-2.5 text-sm text-white focus:outline-none focus:border-accentBlue/50"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">New Password</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
                        <input
                          type={showProfilePassword ? 'text' : 'password'}
                          value={profileForm.password}
                          onChange={(e) => setProfileForm({ ...profileForm, password: e.target.value })}
                          placeholder="Leave blank to keep current password"
                          className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-10 py-2.5 text-sm text-white focus:outline-none focus:border-accentBlue/50"
                        />
                        <button
                          type="button"
                          onClick={() => setShowProfilePassword(!showProfilePassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                        >
                          {showProfilePassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                    {profileForm.password && (
                      <div>
                        <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Confirm New Password</label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
                          <input
                            type={showProfilePassword ? 'text' : 'password'}
                            value={profileForm.confirmPassword}
                            onChange={(e) => setProfileForm({ ...profileForm, confirmPassword: e.target.value })}
                            className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-3 py-2.5 text-sm text-white focus:outline-none focus:border-accentBlue/50"
                          />
                        </div>
                      </div>
                    )}

                    {profileMsg.text && (
                      <p className={`text-sm font-medium ${profileMsg.type === 'error' ? 'text-red-400' : 'text-green-400'}`}>
                        {profileMsg.text}
                      </p>
                    )}

                    <div className="flex gap-3 pt-1">
                      <button
                        type="submit"
                        disabled={profileSaving}
                        className="flex-1 bg-accentBlue text-black font-bold py-2.5 rounded-xl hover:bg-accentBlue/90 transition-colors disabled:opacity-50"
                      >
                        {profileSaving ? 'Saving...' : 'Save Changes'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsEditingProfile(false)}
                        className="px-5 bg-white/5 border border-white/10 text-gray-300 font-bold rounded-xl hover:bg-white/10 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AdminPage;
